/**
 * OpsPortal — generic admin module for any ops system (POS, Security, Dining, CCTV, Housing).
 *
 * Tabs:
 *   • Operations — the system's core entity (sales / incidents / menus / cameras / rooms)
 *   • Team       — member directory (add / edit / remove)
 *
 * Mounted at /:system   (e.g. /pos, /security, /dining, /cctv, /housing)
 * The system code is taken from the URL ``:system`` param.
 */
import React, { useState } from 'react';
import { useLocation, Navigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import {
  ShoppingCart, Shield, UtensilsCrossed, Video, Home,
  Plus, Edit3, Trash2, Loader2, ArrowLeft, Users, AlertTriangle,
  CheckCircle2, LogOut as LogOutIcon, DoorOpen, CalendarCheck,
  Wrench, Brush, Film, ClipboardList, Camera, Activity,
  Clock, MapPin, MessageSquare, Sparkles, Flame, Eye,
} from 'lucide-react';
import { authHeaders } from '@/lib/authToken';
import { useAuthStore } from '@/lib/store';
import { isPlatformTier } from '@/lib/roleTier';
import PrintLabelButton from '@/components/PrintLabelButton';
import { MediaUpload } from '@/components/MediaUpload';
import { MediaUploadList } from '@/components/MediaUploadList';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SYSTEM_META = {
  pos:      { label: 'Point of Sale', icon: ShoppingCart,      accent: 'emerald', entity: 'sales',     entityLabel: 'Sales' },
  security: { label: 'Security',      icon: Shield,            accent: 'red',     entity: 'incidents', entityLabel: 'Incidents' },
  dining:   { label: 'Dining',        icon: UtensilsCrossed,   accent: 'amber',   entity: 'menus',     entityLabel: 'Menus' },
  cctv:     { label: 'CCTV',          icon: Video,             accent: 'indigo',  entity: 'cameras',   entityLabel: 'Cameras' },
  housing:  { label: 'Housing',       icon: Home,              accent: 'teal',    entity: 'rooms',     entityLabel: 'Rooms' },
};

const api = async (path, opts = {}) => {
  const res = await fetch(`${API_URL}${path}`, {
    ...opts,
    headers: { 'Content-Type': 'application/json', ...authHeaders(), ...(opts.headers || {}) },
  });
  if (!res.ok) {
    const e = await res.json().catch(() => ({}));
    throw new Error(e.detail || `Request failed (${res.status})`);
  }
  return res.json();
};

const OpsPortal = () => {
  const { pathname } = useLocation();
  const system = pathname.replace(/^\//, '').split('/')[0];   // /pos → "pos"
  const meta = SYSTEM_META[system];

  // Discover roles for this system + current user's membership
  const { data: me } = useQuery({
    queryKey: ['ops-me', system],
    queryFn: () => api(`/api/ops/${system}/team/me`).catch(() => null),
    enabled: !!meta,
  });
  const { data: systems = [] } = useQuery({
    queryKey: ['ops-systems'],
    queryFn: () => api('/api/ops/systems'),
    enabled: !!meta,
  });

  if (!meta) return <Navigate to="/admin" replace />;
  const Icon = meta.icon;
  const sysCfg = systems.find(s => s.system === system);
  const isManager = me?.is_super || /admin|manager|chief/.test(me?.role || '');

  return (
    <div className={`min-h-screen bg-slate-50`} data-testid={`ops-portal-${system}`}>
      <header className="bg-white border-b">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Link to="/admin/teams" className="text-slate-400 hover:text-slate-700"><ArrowLeft className="h-5 w-5" /></Link>
            <div>
              <h1 className="text-xl font-bold text-slate-900 flex items-center gap-2">
                <Icon className={`h-5 w-5 text-${meta.accent}-600`} />
                {meta.label}
              </h1>
              <p className="text-xs text-slate-500">{me?.role ? `Your role: ${me.role.replace(/_/g, ' ')}` : 'Operations module'}</p>
            </div>
          </div>
          {isManager && (
            <Link
              to={`/admin/teams?tab=${system}`}
              className="text-xs font-medium text-slate-600 hover:text-slate-900 inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-slate-200 hover:bg-slate-50"
              data-testid={`ops-manage-team-${system}`}
            >
              <Users className="h-3.5 w-3.5" />Manage Team
            </Link>
          )}
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-6">
        <EntityTab system={system} meta={meta} />
      </main>
    </div>
  );
};


function EntityTab({ system, meta }) {
  switch (system) {
    case 'pos':      return <PosTab />;
    case 'security': return <SecurityTab />;
    case 'dining':   return <DiningTab />;
    case 'cctv':     return <CctvTab />;
    case 'housing':  return <HousingTab />;
    default:         return null;
  }
}


// ==================== POS ====================
const POS_CATEGORIES = ['food', 'beverage', 'merchandise', 'service', 'ticket', 'supply', 'other'];
const CAT_COLOR = {
  food: 'bg-orange-100 text-orange-800',
  beverage: 'bg-sky-100 text-sky-800',
  merchandise: 'bg-purple-100 text-purple-800',
  service: 'bg-emerald-100 text-emerald-800',
  ticket: 'bg-amber-100 text-amber-800',
  supply: 'bg-slate-100 text-slate-800',
  other: 'bg-gray-100 text-gray-700',
};

function PosTab() {
  const { user } = useAuthStore();
  const qc = useQueryClient();
  const [view, setView] = useState('sales');     // 'sales' | 'products'
  const [adding, setAdding] = useState(false);
  const [showRefunded, setShowRefunded] = useState(false);
  const [paymentFilter, setPaymentFilter] = useState('all');
  const url = `/api/ops/pos/sales?limit=200${paymentFilter === 'all' ? '' : `&payment_method=${paymentFilter}`}${showRefunded ? '' : '&status=completed'}`;
  const { data: sales = [], isLoading } = useQuery({ queryKey: ['pos-sales', paymentFilter, showRefunded], queryFn: () => api(url) });
  const { data: daily = [] } = useQuery({ queryKey: ['pos-daily'], queryFn: () => api('/api/ops/pos/daily-totals?days=7') });
  const { data: stats } = useQuery({ queryKey: ['pos-stats'], queryFn: () => api('/api/ops/pos/stats?days=30') });
  const { data: products = [] } = useQuery({ queryKey: ['pos-products'], queryFn: () => api('/api/ops/pos/products?limit=500') });
  const refundMut = useMutation({
    mutationFn: ({ id, reason }) => api(`/api/ops/pos/sales/${id}/refund`, { method: 'POST', body: JSON.stringify({ reason }) }),
    onSuccess: () => { toast.success('Refunded · ledger reversed'); qc.invalidateQueries({ queryKey: ['pos-sales'] }); qc.invalidateQueries({ queryKey: ['pos-daily'] }); qc.invalidateQueries({ queryKey: ['pos-stats'] }); qc.invalidateQueries({ queryKey: ['pos-products'] }); },
    onError: (e) => toast.error(e.message),
  });

  const lowStockCount = products.filter(p => p.track_stock && p.stock_qty <= 5).length;
  const todayTotals = daily.filter(d => d.date === new Date().toISOString().slice(0, 10));

  return (
    <div className="space-y-4">
      {/* Hero stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card><CardContent className="p-4 flex items-center gap-3">
          <div className="p-2 rounded-lg bg-emerald-100"><ShoppingCart className="h-5 w-5 text-emerald-700" /></div>
          <div>
            <p className="text-[11px] uppercase tracking-wide font-semibold text-slate-500">Today</p>
            <p className="text-2xl font-bold text-emerald-700">{todayTotals.length === 0 ? '—' : todayTotals.map(d => `${d.currency} ${d.total}`).join(' · ')}</p>
          </div>
        </CardContent></Card>
        <Card><CardContent className="p-4 flex items-center gap-3">
          <div className="p-2 rounded-lg bg-blue-100"><Activity className="h-5 w-5 text-blue-700" /></div>
          <div><p className="text-[11px] uppercase tracking-wide font-semibold text-slate-500">Products</p><p className="text-2xl font-bold">{products.length}</p></div>
        </CardContent></Card>
        <Card className={lowStockCount > 0 ? 'border-amber-300 bg-amber-50' : ''}><CardContent className="p-4 flex items-center gap-3">
          <div className={`p-2 rounded-lg ${lowStockCount > 0 ? 'bg-amber-200' : 'bg-slate-100'}`}><AlertTriangle className={`h-5 w-5 ${lowStockCount > 0 ? 'text-amber-700' : 'text-slate-500'}`} /></div>
          <div><p className="text-[11px] uppercase tracking-wide font-semibold text-slate-500">Low stock</p><p className="text-2xl font-bold text-amber-800">{lowStockCount}</p></div>
        </CardContent></Card>
        <Card><CardContent className="p-4 flex items-center gap-3">
          <div className="p-2 rounded-lg bg-rose-100"><Activity className="h-5 w-5 text-rose-700" /></div>
          <div><p className="text-[11px] uppercase tracking-wide font-semibold text-slate-500">Refunds (30d)</p><p className="text-2xl font-bold text-rose-700">{stats?.refunds?.count || 0}</p></div>
        </CardContent></Card>
      </div>

      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Button size="sm" variant={view === 'sales' ? 'default' : 'outline'} onClick={() => setView('sales')} data-testid="pos-view-sales">Sales</Button>
          <Button size="sm" variant={view === 'products' ? 'default' : 'outline'} onClick={() => setView('products')} data-testid="pos-view-products">
            Products{lowStockCount > 0 && <Badge className="ml-2 bg-amber-100 text-amber-800 hover:bg-amber-100">{lowStockCount} low</Badge>}
          </Button>
          {isPlatformTier(user) && (
            <Link to="/admin/finance/revenue" className="text-xs text-emerald-700 hover:underline ml-2" data-testid="pos-revenue-link">Revenue Dashboard →</Link>
          )}
          <Link to="/admin/pos/tabs" className="text-xs text-indigo-700 hover:underline ml-2" data-testid="pos-tabs-link">Open Tabs →</Link>
          <Link to="/admin/pos/registers" className="text-xs text-amber-700 hover:underline ml-2" data-testid="pos-registers-link">Registers →</Link>
        </div>
        {view === 'sales' && (
          <div className="flex gap-2 items-center flex-wrap">
            <Select value={paymentFilter} onValueChange={setPaymentFilter}>
              <SelectTrigger className="h-8 w-32 text-xs" data-testid="pos-filter-method"><SelectValue placeholder="All methods" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All methods</SelectItem>
                {['cash', 'card', 'mobile', 'transfer'].map(p => <SelectItem key={p} value={p} className="capitalize">{p}</SelectItem>)}
              </SelectContent>
            </Select>
            <label className="text-xs text-slate-500 flex items-center gap-1.5">
              <input type="checkbox" checked={showRefunded} onChange={e => setShowRefunded(e.target.checked)} className="rounded" data-testid="pos-show-refunded" />
              Show refunded
            </label>
            <Button onClick={() => setAdding(true)} className="bg-emerald-600 hover:bg-emerald-700" data-testid="pos-add-sale"><Plus className="h-4 w-4 mr-1" /> Record sale</Button>
          </div>
        )}
      </div>

      {view === 'sales' ? (
        <>
          {stats && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="pos-stats">
              <Card><CardHeader className="pb-2"><CardTitle className="text-xs uppercase tracking-wide text-slate-500">Top items (30d)</CardTitle></CardHeader><CardContent className="p-3 pt-0 space-y-1">
                {(stats.top_items || []).slice(0, 5).map(it => (
                  <div key={it.name + it.currency} className="flex justify-between text-xs">
                    <span className="truncate">{it.name} <span className="text-slate-400">× {it.qty}</span></span>
                    <span className="text-emerald-700 font-medium">{it.currency} {it.revenue}</span>
                  </div>
                ))}
                {(stats.top_items || []).length === 0 && <p className="text-xs text-slate-400">No data</p>}
              </CardContent></Card>
              <Card><CardHeader className="pb-2"><CardTitle className="text-xs uppercase tracking-wide text-slate-500">Top cashiers (30d)</CardTitle></CardHeader><CardContent className="p-3 pt-0 space-y-1">
                {(stats.top_cashiers || []).slice(0, 5).map(c => (
                  <div key={c.cashier_id + c.currency} className="flex justify-between text-xs">
                    <span className="truncate">{c.cashier_name} <span className="text-slate-400">· {c.count}</span></span>
                    <span className="text-emerald-700 font-medium">{c.currency} {c.revenue}</span>
                  </div>
                ))}
                {(stats.top_cashiers || []).length === 0 && <p className="text-xs text-slate-400">No data</p>}
              </CardContent></Card>
              <Card><CardHeader className="pb-2"><CardTitle className="text-xs uppercase tracking-wide text-slate-500">By payment method</CardTitle></CardHeader><CardContent className="p-3 pt-0 space-y-1">
                {(stats.payment_methods || []).slice(0, 5).map(m => (
                  <div key={m.method + m.currency} className="flex justify-between text-xs">
                    <span className="capitalize">{m.method} <span className="text-slate-400">· {m.count}</span></span>
                    <span className="text-emerald-700 font-medium">{m.currency} {m.revenue}</span>
                  </div>
                ))}
                {(stats.payment_methods || []).length === 0 && <p className="text-xs text-slate-400">No data</p>}
              </CardContent></Card>
            </div>
          )}

          <Card><CardContent className="p-0">
            {isLoading ? <div className="p-8 text-center text-slate-400"><Loader2 className="h-4 w-4 animate-spin inline mr-2" />Loading…</div>
             : sales.length === 0 ? <div className="p-12 text-center text-slate-400"><ShoppingCart className="h-10 w-10 mx-auto mb-2" />No sales yet</div>
             : <table className="w-full text-sm">
                <thead><tr className="text-xs uppercase tracking-wide text-slate-500 border-b"><th className="text-left py-2 px-3">When</th><th className="text-left py-2 px-3">Items</th><th className="text-left py-2 px-3">Customer</th><th className="text-right py-2 px-3">Total</th><th className="text-left py-2 px-3">Payment</th><th className="text-right py-2 px-3"></th></tr></thead>
                <tbody>
                  {sales.slice(0, 100).map(s => (
                    <tr key={s.id} className={`border-b last:border-0 ${s.status === 'refunded' ? 'opacity-60 bg-rose-50/50' : ''}`} data-testid={`pos-sale-${s.id}`}>
                      <td className="py-2 px-3 text-xs text-slate-600">{new Date(s.created_at).toLocaleString()}<br /><span className="text-[10px] text-slate-400">{s.cashier_name || '—'}</span></td>
                      <td className="py-2 px-3 text-xs text-slate-700">{(s.items || []).map(i => `${i.name}${i.qty > 1 ? ` ×${i.qty}` : ''}`).slice(0, 3).join(', ')}{(s.items || []).length > 3 ? `… +${s.items.length - 3}` : ''}</td>
                      <td className="py-2 px-3 text-slate-600">{s.customer_name || '—'}{s.customer_email && <span className="text-[10px] text-slate-400 block truncate">{s.customer_email}</span>}</td>
                      <td className="py-2 px-3 text-right font-bold text-emerald-700">{s.currency} {s.total}{s.status === 'refunded' && <span className="block text-[10px] text-rose-600">Refunded</span>}{s.ledger_posted && <span className="block text-[9px] text-emerald-500" title="Posted to Bank ledger">✓ ledger</span>}</td>
                      <td className="py-2 px-3 text-xs text-slate-500 capitalize">{s.payment_method}</td>
                      <td className="py-2 px-3 text-right">
                        {s.status !== 'refunded' && (
                          <Button
                            size="sm" variant="outline" className="h-7 text-xs text-rose-700"
                            onClick={() => { const r = window.prompt('Refund reason (optional)?'); if (r !== null) refundMut.mutate({ id: s.id, reason: r }); }}
                            data-testid={`pos-refund-${s.id}`}
                          >Refund</Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>}
          </CardContent></Card>
        </>
      ) : (
        <PosProductsView products={products} />
      )}
      {adding && <PosAddDialog products={products} onClose={() => setAdding(false)} onAdded={() => { qc.invalidateQueries({ queryKey: ['pos-sales'] }); qc.invalidateQueries({ queryKey: ['pos-daily'] }); qc.invalidateQueries({ queryKey: ['pos-stats'] }); qc.invalidateQueries({ queryKey: ['pos-products'] }); setAdding(false); }} />}
    </div>
  );
}

function PosProductsView({ products }) {
  const qc = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState(null);
  const [adjusting, setAdjusting] = useState(null);
  const [search, setSearch] = useState('');
  const [catFilter, setCatFilter] = useState('all');
  const delMut = useMutation({
    mutationFn: (id) => api(`/api/ops/pos/products/${id}`, { method: 'DELETE' }),
    onSuccess: () => { toast.success('Removed'); qc.invalidateQueries({ queryKey: ['pos-products'] }); },
    onError: (e) => toast.error(e.message),
  });
  const filtered = products.filter(p =>
    (catFilter === 'all' || p.category === catFilter) &&
    (!search || p.name.toLowerCase().includes(search.toLowerCase()) || (p.sku || '').toLowerCase().includes(search.toLowerCase()))
  );
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex gap-2 flex-1 max-w-md">
          <Input placeholder="Search name or SKU…" value={search} onChange={e => setSearch(e.target.value)} className="h-9" data-testid="pos-product-search" />
          <Select value={catFilter} onValueChange={setCatFilter}>
            <SelectTrigger className="h-9 w-36 text-xs" data-testid="pos-product-cat-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All categories</SelectItem>
              {POS_CATEGORIES.map(c => <SelectItem key={c} value={c} className="capitalize">{c}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <Button onClick={() => setAdding(true)} className="bg-emerald-600 hover:bg-emerald-700" data-testid="pos-product-add"><Plus className="h-4 w-4 mr-1" /> New product</Button>
      </div>

      {filtered.length === 0 ? <Card><CardContent className="p-16 text-center text-slate-400"><ShoppingCart className="h-12 w-12 mx-auto mb-3 opacity-50" /><p className="text-sm">No products match the filters</p></CardContent></Card>
       : <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {filtered.map(p => {
            const low = p.track_stock && p.stock_qty <= 5;
            return (
              <Card key={p.id} className={`overflow-hidden hover:shadow-md transition ${!p.is_active ? 'opacity-60' : ''} ${low ? 'border-amber-300' : ''}`} data-testid={`pos-product-${p.id}`}>
                <CardContent className="p-3">
                  <div className="flex items-start gap-3">
                    {p.image_url ? <img src={p.image_url} alt="" className="h-14 w-14 object-cover rounded-lg shrink-0" /> : <div className="h-14 w-14 rounded-lg bg-gradient-to-br from-emerald-100 to-teal-100 flex items-center justify-center shrink-0"><ShoppingCart className="h-6 w-6 text-emerald-600" /></div>}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-1">
                        <p className="font-semibold text-sm truncate">{p.name}</p>
                        <span className={`text-[10px] px-1.5 py-0.5 rounded ${CAT_COLOR[p.category] || ''} capitalize shrink-0`}>{p.category}</span>
                      </div>
                      {p.sku && <p className="text-[10px] text-slate-400 font-mono">{p.sku}</p>}
                      <p className="text-base font-bold text-emerald-700 mt-1">{p.currency} {p.price?.toFixed(2)}</p>
                      <p className={`text-[11px] mt-0.5 ${low ? 'text-amber-700 font-semibold' : 'text-slate-500'}`}>
                        Stock: {p.track_stock ? `${p.stock_qty}${low ? ' · LOW' : ''}` : '—'}{!p.is_active && ' · Inactive'}
                      </p>
                    </div>
                  </div>
                  <div className="flex justify-end gap-1 mt-2 pt-2 border-t">
                    <PrintLabelButton entity="product" id={p.id} name={p.name} />
                    <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => setAdjusting(p)} data-testid={`pos-product-stock-${p.id}`} title="Adjust stock">+/-</Button>
                    <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => setEditing(p)} data-testid={`pos-product-edit-${p.id}`}><Edit3 className="h-3 w-3" /></Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-rose-600" onClick={() => { if (window.confirm(`Delete ${p.name}?`)) delMut.mutate(p.id); }} data-testid={`pos-product-delete-${p.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>}

      {adding && <PosProductDialog onClose={() => setAdding(false)} onSaved={() => { qc.invalidateQueries({ queryKey: ['pos-products'] }); setAdding(false); }} />}
      {editing && <PosProductDialog product={editing} onClose={() => setEditing(null)} onSaved={() => { qc.invalidateQueries({ queryKey: ['pos-products'] }); setEditing(null); }} />}
      {adjusting && <PosStockAdjustDialog product={adjusting} onClose={() => setAdjusting(null)} onSaved={() => { qc.invalidateQueries({ queryKey: ['pos-products'] }); setAdjusting(null); }} />}
    </div>
  );
}

function PosProductDialog({ product, onClose, onSaved }) {
  const isEdit = !!product;
  const [form, setForm] = useState(product ? {
    name: product.name || '', sku: product.sku || '', category: product.category || 'other',
    price: product.price || 0, currency: product.currency || 'USD',
    stock_qty: product.stock_qty || 0, track_stock: product.track_stock !== false,
    is_active: product.is_active !== false, image_url: product.image_url || '',
    description: product.description || '',
  } : {
    name: '', sku: '', category: 'other', price: 0, currency: 'USD',
    stock_qty: 0, track_stock: true, is_active: true, image_url: '', description: '',
  });
  const mut = useMutation({
    mutationFn: () => api(isEdit ? `/api/ops/pos/products/${product.id}` : '/api/ops/pos/products',
      { method: isEdit ? 'PUT' : 'POST', body: JSON.stringify({
        ...form, price: parseFloat(form.price) || 0, stock_qty: parseInt(form.stock_qty, 10) || 0,
      }) }),
    onSuccess: () => { toast.success(isEdit ? 'Saved' : 'Product created'); onSaved?.(); },
    onError: (e) => toast.error(e.message),
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="pos-product-dialog">
        <DialogHeader><DialogTitle>{isEdit ? 'Edit product' : 'New product'}</DialogTitle><DialogDescription>Add to the catalogue — appears in cashiers' product picker.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            <div className="col-span-2"><Label>Name *</Label><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="pos-product-name" /></div>
            <div><Label>SKU</Label><Input value={form.sku} onChange={e => setForm({ ...form, sku: e.target.value.toUpperCase() })} placeholder="CAP-001" data-testid="pos-product-sku" /></div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            <div><Label>Price *</Label><Input type="number" step="0.01" min={0} value={form.price} onChange={e => setForm({ ...form, price: e.target.value })} data-testid="pos-product-price" /></div>
            <div><Label>Currency</Label><Input value={form.currency} onChange={e => setForm({ ...form, currency: e.target.value.toUpperCase() })} /></div>
            <div>
              <Label>Category</Label>
              <Select value={form.category} onValueChange={v => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid="pos-product-category"><SelectValue /></SelectTrigger>
                <SelectContent>{POS_CATEGORIES.map(c => <SelectItem key={c} value={c} className="capitalize">{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label>Stock qty</Label><Input type="number" min={0} value={form.stock_qty} onChange={e => setForm({ ...form, stock_qty: e.target.value })} data-testid="pos-product-stock" /></div>
            <MediaUpload value={form.image_url} onChange={(url) => setForm({ ...form, image_url: url })} purpose="product" label="Picture" testId="pos-product-image-upload" previewClassName="h-16" />
          </div>
          <div><Label>Description</Label><Textarea rows={2} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} /></div>
          <div className="flex items-center gap-4 pt-1 border-t">
            <label className="text-xs flex items-center gap-1.5"><input type="checkbox" checked={form.track_stock} onChange={e => setForm({ ...form, track_stock: e.target.checked })} className="rounded" />Track stock</label>
            <label className="text-xs flex items-center gap-1.5"><input type="checkbox" checked={form.is_active} onChange={e => setForm({ ...form, is_active: e.target.checked })} className="rounded" />Active</label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => { if (!form.name) return toast.error('Name required'); mut.mutate(); }} disabled={mut.isPending} className="bg-emerald-600 hover:bg-emerald-700" data-testid="pos-product-save">
            {mut.isPending && <Loader2 className="h-4 w-4 animate-spin mr-1" />}{isEdit ? 'Save' : 'Create'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PosStockAdjustDialog({ product, onClose, onSaved }) {
  const [form, setForm] = useState({ delta: 0, reason: '' });
  const mut = useMutation({
    mutationFn: () => api(`/api/ops/pos/products/${product.id}/stock-adjust`, { method: 'POST', body: JSON.stringify({
      delta: parseInt(form.delta, 10) || 0, reason: form.reason,
    }) }),
    onSuccess: () => { toast.success('Stock adjusted'); onSaved?.(); },
    onError: (e) => toast.error(e.message),
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="pos-stock-dialog">
        <DialogHeader><DialogTitle>Adjust stock — {product.name}</DialogTitle><DialogDescription>Positive to receive shipment, negative to write off damage. Current stock: {product.stock_qty}.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div><Label>Delta *</Label><Input type="number" value={form.delta} onChange={e => setForm({ ...form, delta: e.target.value })} placeholder="+10 or -2" data-testid="pos-stock-delta" /></div>
          <div><Label>Reason</Label><Input value={form.reason} onChange={e => setForm({ ...form, reason: e.target.value })} placeholder="Shipment received / Damaged / Stocktake" /></div>
          <p className="text-xs text-slate-500">New stock: <span className="font-bold">{product.stock_qty + (parseInt(form.delta, 10) || 0)}</span></p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => { if (!form.delta) return toast.error('Delta required'); mut.mutate(); }} disabled={mut.isPending} data-testid="pos-stock-save">
            {mut.isPending && <Loader2 className="h-4 w-4 animate-spin mr-1" />}Adjust
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PosAddDialog({ products, onClose, onAdded }) {
  const [cart, setCart] = useState([]);   // [{product_id?, name, price, qty}]
  const [search, setSearch] = useState('');
  const [meta, setMeta] = useState({ currency: 'USD', payment_method: 'cash', customer_name: '', customer_email: '', notes: '' });
  const [customName, setCustomName] = useState('');
  const [customPrice, setCustomPrice] = useState('');

  const addProduct = (p) => {
    if (p.track_stock && p.stock_qty <= 0) { toast.error(`${p.name} is out of stock`); return; }
    setCart(c => {
      const i = c.findIndex(x => x.product_id === p.id);
      if (i >= 0) {
        if (p.track_stock && c[i].qty + 1 > p.stock_qty) { toast.error(`Only ${p.stock_qty} ${p.name} in stock`); return c; }
        return c.map((x, idx) => idx === i ? { ...x, qty: x.qty + 1 } : x);
      }
      return [...c, { product_id: p.id, name: p.name, price: p.price, qty: 1, currency: p.currency }];
    });
    if (cart.length === 0) setMeta(m => ({ ...m, currency: p.currency || 'USD' }));
  };
  const addCustom = () => {
    if (!customName.trim() || !customPrice) { toast.error('Custom item needs name + price'); return; }
    setCart(c => [...c, { name: customName.trim(), price: parseFloat(customPrice) || 0, qty: 1 }]);
    setCustomName(''); setCustomPrice('');
  };
  const updateQty = (i, q) => setCart(c => c.map((x, idx) => idx === i ? { ...x, qty: Math.max(1, q) } : x));
  const removeLine = (i) => setCart(c => c.filter((_, idx) => idx !== i));

  const total = cart.reduce((sum, x) => sum + (parseFloat(x.price) || 0) * (parseInt(x.qty, 10) || 1), 0);
  const filtered = products.filter(p => p.is_active !== false && (!search || p.name.toLowerCase().includes(search.toLowerCase()) || (p.sku || '').toLowerCase().includes(search.toLowerCase()))).slice(0, 24);

  const mut = useMutation({
    mutationFn: () => api('/api/ops/pos/sales', { method: 'POST', body: JSON.stringify({
      items: cart.map(x => ({ name: x.name, price: x.price, qty: x.qty, product_id: x.product_id || undefined })),
      total: parseFloat(total.toFixed(2)),
      currency: meta.currency, payment_method: meta.payment_method,
      customer_name: meta.customer_name, customer_email: meta.customer_email, notes: meta.notes,
    }) }),
    onSuccess: (resp) => {
      toast.success(`Sale recorded · ${meta.currency} ${total.toFixed(2)}` + (resp.ledger_posted ? ' · ledger ✓' : '') + (meta.customer_email ? ' · receipt sent' : ''));
      onAdded?.();
    },
    onError: (e) => toast.error(e.message),
  });

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="pos-add-dialog">
        <DialogHeader><DialogTitle>Record sale</DialogTitle><DialogDescription>Tap products to build the cart, or add a custom line. Total auto-computed.</DialogDescription></DialogHeader>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
          {/* Product picker */}
          <div className="md:col-span-3 space-y-2">
            <Input placeholder="Search products…" value={search} onChange={e => setSearch(e.target.value)} className="h-9" data-testid="pos-cart-search" />
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-72 overflow-y-auto pr-1">
              {filtered.length === 0 ? <p className="col-span-3 text-xs text-slate-400 text-center py-8">{products.length === 0 ? 'No products yet — switch to Products tab to add some' : 'No matches'}</p>
               : filtered.map(p => {
                  const oos = p.track_stock && p.stock_qty <= 0;
                  return (
                    <button key={p.id} type="button" disabled={oos} onClick={() => addProduct(p)}
                      className={`text-left p-2 border rounded-lg transition ${oos ? 'opacity-50 cursor-not-allowed bg-slate-50' : 'hover:border-emerald-400 hover:bg-emerald-50'}`}
                      data-testid={`pos-cart-product-${p.id}`}
                    >
                      <p className="text-xs font-medium truncate">{p.name}</p>
                      <p className="text-sm font-bold text-emerald-700">{p.currency} {p.price?.toFixed(2)}</p>
                      <p className="text-[10px] text-slate-400">{p.track_stock ? (oos ? 'Out of stock' : `${p.stock_qty} in stock`) : 'No track'}</p>
                    </button>
                  );
                })}
            </div>
            <div className="border-t pt-2">
              <Label className="text-xs">Or add custom item</Label>
              <div className="flex gap-1.5">
                <Input placeholder="Name" value={customName} onChange={e => setCustomName(e.target.value)} className="h-8 text-xs" data-testid="pos-cart-custom-name" />
                <Input type="number" step="0.01" placeholder="Price" value={customPrice} onChange={e => setCustomPrice(e.target.value)} className="h-8 w-20 text-xs" data-testid="pos-cart-custom-price" />
                <Button type="button" size="sm" variant="outline" onClick={addCustom} data-testid="pos-cart-custom-add">+</Button>
              </div>
            </div>
          </div>
          {/* Cart + checkout */}
          <div className="md:col-span-2 space-y-2 bg-slate-50 p-3 rounded-lg">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Cart ({cart.length})</p>
            <div className="max-h-44 overflow-y-auto space-y-1">
              {cart.length === 0 ? <p className="text-xs text-slate-400 italic">Empty cart</p>
               : cart.map((x, i) => (
                  <div key={i} className="flex items-center gap-1 text-xs bg-white p-1.5 rounded border" data-testid={`pos-cart-line-${i}`}>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{x.name}</p>
                      <p className="text-[10px] text-slate-500">{meta.currency} {x.price?.toFixed(2)} ea</p>
                    </div>
                    <Input type="number" min={1} value={x.qty} onChange={e => updateQty(i, parseInt(e.target.value, 10) || 1)} className="h-7 w-12 text-xs" data-testid={`pos-cart-qty-${i}`} />
                    <Button variant="ghost" size="icon" className="h-6 w-6 text-rose-600" onClick={() => removeLine(i)} data-testid={`pos-cart-remove-${i}`}><Trash2 className="h-3 w-3" /></Button>
                  </div>
                ))}
            </div>
            <div className="flex justify-between items-center border-t pt-2">
              <span className="text-sm font-semibold">Total</span>
              <span className="text-xl font-bold text-emerald-700" data-testid="pos-cart-total">{meta.currency} {total.toFixed(2)}</span>
            </div>
            <div>
              <Label className="text-xs">Payment</Label>
              <Select value={meta.payment_method} onValueChange={(v) => setMeta({ ...meta, payment_method: v })}>
                <SelectTrigger className="h-8 text-xs" data-testid="pos-cart-payment"><SelectValue /></SelectTrigger>
                <SelectContent>{['cash','card','mobile','transfer'].map(p => <SelectItem key={p} value={p} className="capitalize">{p}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs">Customer (optional)</Label><Input value={meta.customer_name} onChange={e => setMeta({ ...meta, customer_name: e.target.value })} className="h-8 text-xs" /></div>
            <div><Label className="text-xs">Email <span className="text-slate-400">(receipt)</span></Label><Input type="email" value={meta.customer_email} onChange={e => setMeta({ ...meta, customer_email: e.target.value })} className="h-8 text-xs" data-testid="pos-cart-email" /></div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => { if (cart.length === 0) return toast.error('Cart is empty'); mut.mutate(); }} disabled={mut.isPending || cart.length === 0} className="bg-emerald-600 hover:bg-emerald-700" data-testid="pos-cart-checkout">
            {mut.isPending && <Loader2 className="h-4 w-4 animate-spin mr-1" />}Charge {meta.currency} {total.toFixed(2)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ==================== SECURITY ====================
const SEV_STYLES = {
  low:      { tile: 'bg-blue-50 border-blue-200',   text: 'text-blue-800',   pill: 'bg-blue-100 text-blue-800',     bar: 'bg-blue-500' },
  medium:   { tile: 'bg-amber-50 border-amber-200', text: 'text-amber-800',  pill: 'bg-amber-100 text-amber-800',   bar: 'bg-amber-500' },
  high:     { tile: 'bg-rose-50 border-rose-200',   text: 'text-rose-800',   pill: 'bg-rose-100 text-rose-800',     bar: 'bg-rose-500' },
  critical: { tile: 'bg-red-50 border-red-300 ring-1 ring-red-200', text: 'text-red-900', pill: 'bg-red-600 text-white animate-pulse', bar: 'bg-red-700' },
};
const STATUS_STYLES = {
  open:           { pill: 'bg-rose-100 text-rose-800 border-rose-200',     icon: AlertTriangle },
  acknowledged:   { pill: 'bg-amber-100 text-amber-800 border-amber-200',  icon: Eye },
  investigating:  { pill: 'bg-indigo-100 text-indigo-800 border-indigo-200', icon: Activity },
  resolved:       { pill: 'bg-emerald-100 text-emerald-800 border-emerald-200', icon: CheckCircle2 },
  closed:         { pill: 'bg-slate-100 text-slate-600 border-slate-200',  icon: CheckCircle2 },
};

function SecurityTab() {
  const qc = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [detailId, setDetailId] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [sevFilter, setSevFilter] = useState('all');
  const url = `/api/ops/security/incidents?limit=200${statusFilter === 'all' ? '' : `&status=${statusFilter}`}${sevFilter === 'all' ? '' : `&severity=${sevFilter}`}`;
  const { data: incidents = [], isLoading } = useQuery({ queryKey: ['sec-incidents', statusFilter, sevFilter], queryFn: () => api(url) });
  const { data: stats } = useQuery({ queryKey: ['sec-stats'], queryFn: () => api('/api/ops/security/incidents/stats?days=30') });
  const updMut = useMutation({
    mutationFn: ({ id, status }) => api(`/api/ops/security/incidents/${id}`, { method: 'PUT', body: JSON.stringify({ status }) }),
    onSuccess: () => { toast.success('Updated'); qc.invalidateQueries({ queryKey: ['sec-incidents'] }); qc.invalidateQueries({ queryKey: ['sec-stats'] }); },
    onError: (e) => toast.error(e.message),
  });

  const totals = Object.values(stats?.by_status || {}).reduce((a, b) => a + b, 0);
  const resolveRate = totals > 0 ? Math.round(((stats?.by_status?.resolved || 0) / totals) * 100) : 0;

  return (
    <div className="space-y-4">
      {/* Stats hero strip */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="sec-stats">
          <Card className={`border-2 ${(stats.critical_open || 0) > 0 ? 'border-red-400 bg-red-50' : 'border-slate-200'}`}>
            <CardContent className="p-4 flex items-center gap-3">
              <div className={`p-2 rounded-lg ${(stats.critical_open || 0) > 0 ? 'bg-red-600' : 'bg-slate-200'}`}>
                <Flame className={`h-5 w-5 ${(stats.critical_open || 0) > 0 ? 'text-white animate-pulse' : 'text-slate-500'}`} />
              </div>
              <div>
                <p className="text-[11px] uppercase tracking-wide font-semibold text-slate-500">Critical open</p>
                <p className="text-2xl font-bold text-red-900">{stats.critical_open ?? 0}</p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-2 border-rose-200">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-2 rounded-lg bg-rose-100"><AlertTriangle className="h-5 w-5 text-rose-700" /></div>
              <div>
                <p className="text-[11px] uppercase tracking-wide font-semibold text-slate-500">Open</p>
                <p className="text-2xl font-bold text-rose-700">{stats.open_total ?? 0}</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-2 rounded-lg bg-emerald-100"><CheckCircle2 className="h-5 w-5 text-emerald-700" /></div>
              <div>
                <p className="text-[11px] uppercase tracking-wide font-semibold text-slate-500">Resolve rate</p>
                <p className="text-2xl font-bold text-emerald-700">{resolveRate}<span className="text-sm">%</span></p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-2 rounded-lg bg-slate-100"><Activity className="h-5 w-5 text-slate-700" /></div>
              <div>
                <p className="text-[11px] uppercase tracking-wide font-semibold text-slate-500">30d total</p>
                <p className="text-2xl font-bold">{totals}</p>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      <div className="flex items-center justify-between gap-2 flex-wrap">
        <h2 className="text-base font-semibold flex items-center gap-2"><Shield className="h-4 w-4 text-red-600" />Incident reports</h2>
        <div className="flex gap-2 items-center flex-wrap">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="h-8 w-32 text-xs" data-testid="sec-filter-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {['open', 'acknowledged', 'investigating', 'resolved', 'closed'].map(s => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={sevFilter} onValueChange={setSevFilter}>
            <SelectTrigger className="h-8 w-32 text-xs" data-testid="sec-filter-severity"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All severities</SelectItem>
              {['low', 'medium', 'high', 'critical'].map(s => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button onClick={() => setAdding(true)} className="bg-red-600 hover:bg-red-700" data-testid="sec-add-incident"><Plus className="h-4 w-4 mr-1" /> Report incident</Button>
        </div>
      </div>

      {isLoading ? <Card><CardContent className="p-12 text-center text-slate-400"><Loader2 className="h-5 w-5 animate-spin inline mr-2" />Loading…</CardContent></Card>
       : incidents.length === 0 ? <Card><CardContent className="p-16 text-center text-slate-400"><Shield className="h-12 w-12 mx-auto mb-3 opacity-50" /><p className="text-sm">No incidents match the filters</p></CardContent></Card>
       : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {incidents.map(i => {
            const sev = SEV_STYLES[i.severity] || SEV_STYLES.low;
            const st = STATUS_STYLES[i.status] || STATUS_STYLES.open;
            const StIcon = st.icon;
            return (
              <Card key={i.id} className={`overflow-hidden border-l-4 ${i.severity === 'critical' ? 'border-l-red-700' : i.severity === 'high' ? 'border-l-rose-500' : i.severity === 'medium' ? 'border-l-amber-500' : 'border-l-blue-500'} hover:shadow-md transition-shadow`} data-testid={`sec-incident-${i.id}`}>
                <CardContent className="p-4 space-y-2.5">
                  <div className="flex items-start justify-between gap-2">
                    <button className="text-left flex-1" onClick={() => setDetailId(i.id)} data-testid={`sec-incident-open-${i.id}`}>
                      <h3 className="font-semibold text-sm text-slate-900 hover:text-red-700 transition-colors">{i.title}</h3>
                      {i.description && <p className="text-xs text-slate-500 line-clamp-2 mt-0.5">{i.description}</p>}
                    </button>
                    <Badge className={`text-[10px] font-bold ${sev.pill} capitalize shrink-0`}>{i.severity}</Badge>
                  </div>
                  <div className="flex flex-wrap items-center gap-2 text-[11px] text-slate-500">
                    {i.location && <span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{i.location}</span>}
                    {i.reporter_name && <span className="flex items-center gap-1"><Users className="h-3 w-3" />{i.reporter_name}</span>}
                    <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{new Date(i.created_at).toLocaleString()}</span>
                    {(i.photo_urls || []).length > 0 && <span className="flex items-center gap-1 text-indigo-600"><Camera className="h-3 w-3" />{i.photo_urls.length}</span>}
                    {(i.timeline || []).filter(t => t.kind === 'cctv_clip').length > 0 && <span className="flex items-center gap-1 text-purple-600"><Film className="h-3 w-3" />{(i.timeline || []).filter(t => t.kind === 'cctv_clip').length}</span>}
                  </div>
                  <div className="flex items-center justify-between pt-2 border-t border-slate-100">
                    <Badge variant="outline" className={`text-[10px] gap-1 capitalize ${st.pill}`}>
                      <StIcon className="h-3 w-3" />{i.status}
                    </Badge>
                    <div className="flex gap-1.5">
                      {i.status === 'open' && <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => updMut.mutate({ id: i.id, status: 'acknowledged' })} data-testid={`sec-ack-${i.id}`}>Ack</Button>}
                      {i.status === 'acknowledged' && <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => updMut.mutate({ id: i.id, status: 'investigating' })} data-testid={`sec-invest-${i.id}`}>Investigate</Button>}
                      {(i.status === 'investigating' || i.status === 'acknowledged' || i.status === 'open') && (
                        <Button size="sm" className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700" onClick={() => updMut.mutate({ id: i.id, status: 'resolved' })} data-testid={`sec-resolve-${i.id}`}><CheckCircle2 className="h-3 w-3 mr-1" />Resolve</Button>
                      )}
                      {i.status === 'resolved' && <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => updMut.mutate({ id: i.id, status: 'closed' })} data-testid={`sec-close-${i.id}`}>Close</Button>}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {adding && <SecAddDialog onClose={() => setAdding(false)} onAdded={() => { qc.invalidateQueries({ queryKey: ['sec-incidents'] }); qc.invalidateQueries({ queryKey: ['sec-stats'] }); setAdding(false); }} />}
      {detailId && <SecDetailDialog id={detailId} onClose={() => setDetailId(null)} />}
    </div>
  );
}

function SecAddDialog({ onClose, onAdded }) {
  const [form, setForm] = useState({ title: '', location: '', severity: 'low', description: '', photo_urls: '' });
  const mut = useMutation({
    mutationFn: () => api('/api/ops/security/incidents', { method: 'POST', body: JSON.stringify({
      ...form,
      photo_urls: form.photo_urls.split(',').map(s => s.trim()).filter(Boolean),
    }) }),
    onSuccess: () => { toast.success('Incident reported'); onAdded?.(); },
    onError: (e) => toast.error(e.message),
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="sec-add-dialog">
        <DialogHeader><DialogTitle>Report incident</DialogTitle><DialogDescription>What happened, where, and how serious is it?</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div><Label>Title *</Label><Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="Unauthorized access at gate B" data-testid="sec-add-title" /></div>
          <div><Label>Location</Label><Input value={form.location} onChange={e => setForm({ ...form, location: e.target.value })} /></div>
          <div>
            <Label>Severity</Label>
            <Select value={form.severity} onValueChange={(v) => setForm({ ...form, severity: v })}>
              <SelectTrigger data-testid="sec-add-severity"><SelectValue /></SelectTrigger>
              <SelectContent>{['low','medium','high','critical'].map(s => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Description</Label><Textarea rows={3} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} /></div>
          <div><Label>Evidence photos</Label>
            <MediaUploadList
              value={(form.photo_urls || '').split(',').map(s => s.trim()).filter(Boolean)}
              onChange={(list) => setForm({ ...form, photo_urls: list.join(', ') })}
              purpose="photo" hint="Add an evidence photo" testId="sec-add-photos" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => { if (!form.title) return toast.error('Title required'); mut.mutate(); }} disabled={mut.isPending} data-testid="sec-add-save">
            {mut.isPending && <Loader2 className="h-4 w-4 animate-spin mr-1" />}<AlertTriangle className="h-4 w-4 mr-1" />Report
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SecDetailDialog({ id, onClose }) {
  const qc = useQueryClient();
  const { data: incidents = [] } = useQuery({ queryKey: ['sec-incidents'], queryFn: () => api('/api/ops/security/incidents?limit=500') });
  const { data: clips = [] } = useQuery({ queryKey: ['sec-incident-clips', id], queryFn: () => api(`/api/ops/cctv/clips?incident_id=${id}`).catch(() => []) });
  const inc = incidents.find(x => x.id === id);
  const [comment, setComment] = useState('');
  const [showLinkClip, setShowLinkClip] = useState(false);
  const commentMut = useMutation({
    mutationFn: () => api(`/api/ops/security/incidents/${id}/comments`, { method: 'POST', body: JSON.stringify({ text: comment }) }),
    onSuccess: () => { toast.success('Comment added'); setComment(''); qc.invalidateQueries({ queryKey: ['sec-incidents'] }); },
    onError: (e) => toast.error(e.message),
  });
  if (!inc) return null;
  const sev = SEV_STYLES[inc.severity] || SEV_STYLES.low;
  const st = STATUS_STYLES[inc.status] || STATUS_STYLES.open;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="sec-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Shield className="h-5 w-5 text-red-600" />{inc.title}</DialogTitle>
          <DialogDescription>Incident details, evidence, timeline and comments.</DialogDescription>
        </DialogHeader>
        <div className="flex flex-wrap items-center gap-2 -mt-1 mb-1">
          <Badge className={`text-[10px] font-bold ${sev.pill} capitalize`}>{inc.severity}</Badge>
          <Badge variant="outline" className={`text-[10px] capitalize ${st.pill}`}>{inc.status}</Badge>
          {inc.location && <span className="text-xs text-slate-500 flex items-center gap-1"><MapPin className="h-3 w-3" />{inc.location}</span>}
          {inc.reporter_name && <span className="text-xs text-slate-500 flex items-center gap-1"><Users className="h-3 w-3" />{inc.reporter_name}</span>}
          <span className="text-xs text-slate-500 flex items-center gap-1"><Clock className="h-3 w-3" />{new Date(inc.created_at).toLocaleString()}</span>
        </div>
        <div className="space-y-4">
          {inc.description && <div><p className="text-xs text-slate-500 uppercase tracking-wide mb-1 font-semibold">Description</p><p className="text-sm whitespace-pre-wrap bg-slate-50 p-3 rounded-lg">{inc.description}</p></div>}

          {(inc.photo_urls || []).length > 0 && (
            <div><p className="text-xs text-slate-500 uppercase tracking-wide mb-1.5 font-semibold flex items-center gap-1"><Camera className="h-3 w-3" />Evidence Photos</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {inc.photo_urls.map((u, i) => (
                  <a key={i} href={u} target="_blank" rel="noopener noreferrer" data-testid={`sec-photo-${i}`}>
                    <img src={u} alt="" className="w-full aspect-square object-cover rounded-lg border hover:opacity-90 transition" />
                  </a>
                ))}
              </div>
            </div>
          )}

          <div>
            <div className="flex items-center justify-between mb-1.5">
              <p className="text-xs text-slate-500 uppercase tracking-wide font-semibold flex items-center gap-1"><Film className="h-3 w-3" />CCTV Clips {clips.length > 0 && <span className="text-slate-400">({clips.length})</span>}</p>
              <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setShowLinkClip(true)} data-testid="sec-link-clip">
                <Plus className="h-3 w-3 mr-1" />Link clip
              </Button>
            </div>
            {clips.length === 0 ? <p className="text-xs text-slate-400 italic">No clips linked yet.</p>
             : <div className="grid grid-cols-2 gap-2">
                {clips.map(c => (
                  <a key={c.id} href={c.clip_url} target="_blank" rel="noopener noreferrer" className="block bg-slate-900 rounded-lg overflow-hidden hover:ring-2 hover:ring-purple-400 transition" data-testid={`sec-clip-${c.id}`}>
                    {c.thumbnail_url ? <img src={c.thumbnail_url} alt="" className="w-full aspect-video object-cover opacity-90" /> : <div className="w-full aspect-video flex items-center justify-center bg-gradient-to-br from-slate-800 to-slate-900"><Film className="h-8 w-8 text-purple-400" /></div>}
                    <div className="p-2 text-xs text-white">
                      <p className="font-medium truncate">{c.camera_name || 'Camera'}</p>
                      <p className="text-slate-300 text-[10px]">{c.captured_at?.slice(0, 16).replace('T', ' ')}</p>
                    </div>
                  </a>
                ))}
              </div>}
          </div>

          <div><p className="text-xs text-slate-500 uppercase tracking-wide mb-1.5 font-semibold flex items-center gap-1"><Activity className="h-3 w-3" />Timeline</p>
            {(inc.timeline || []).length === 0 ? <p className="text-xs text-slate-400 italic">No timeline entries.</p>
             : <div className="space-y-1.5">
                {(inc.timeline || []).map((t, i) => (
                  <div key={i} className="border-l-2 border-slate-300 pl-3 py-1 text-sm" data-testid={`sec-timeline-${i}`}>
                    <p className="text-[11px] text-slate-500">{t.by_name || 'system'} · {t.at?.slice(0, 16).replace('T', ' ')} · <span className="capitalize text-slate-400">{t.kind?.replace(/_/g, ' ')}</span></p>
                    <p className="text-slate-700">{t.text}</p>
                  </div>
                ))}
              </div>}
          </div>

          <div className="border-t pt-3">
            <Label className="flex items-center gap-1 mb-1.5"><MessageSquare className="h-3.5 w-3.5" />Add comment</Label>
            <div className="flex gap-2">
              <Input value={comment} onChange={e => setComment(e.target.value)} placeholder="Note for the team…" data-testid="sec-comment-input" onKeyDown={e => { if (e.key === 'Enter' && comment.trim()) commentMut.mutate(); }} />
              <Button onClick={() => comment.trim() && commentMut.mutate()} disabled={!comment.trim() || commentMut.isPending} data-testid="sec-comment-add">
                {commentMut.isPending && <Loader2 className="h-4 w-4 animate-spin mr-1" />}Add
              </Button>
            </div>
          </div>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Close</Button></DialogFooter>
        {showLinkClip && <LinkClipDialog incidentId={id} onClose={() => setShowLinkClip(false)} onLinked={() => { setShowLinkClip(false); qc.invalidateQueries({ queryKey: ['sec-incident-clips', id] }); qc.invalidateQueries({ queryKey: ['sec-incidents'] }); }} />}
      </DialogContent>
    </Dialog>
  );
}

function LinkClipDialog({ incidentId, onClose, onLinked }) {
  const { data: cameras = [] } = useQuery({ queryKey: ['cctv-cameras'], queryFn: () => api('/api/ops/cctv/cameras').catch(() => []) });
  const [form, setForm] = useState({ camera_id: '', clip_url: '', thumbnail_url: '', captured_at: new Date().toISOString().slice(0, 16), duration_seconds: 0, notes: '' });
  const mut = useMutation({
    mutationFn: () => api('/api/ops/cctv/clips', { method: 'POST', body: JSON.stringify({ ...form, incident_id: incidentId, captured_at: new Date(form.captured_at).toISOString() }) }),
    onSuccess: () => { toast.success('Clip linked'); onLinked?.(); },
    onError: (e) => toast.error(e.message),
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="sec-link-clip-dialog">
        <DialogHeader><DialogTitle>Link CCTV clip</DialogTitle><DialogDescription>Attach footage as evidence for this incident.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Camera *</Label>
            <Select value={form.camera_id} onValueChange={v => setForm({ ...form, camera_id: v })}>
              <SelectTrigger data-testid="sec-link-clip-camera"><SelectValue placeholder="Choose camera" /></SelectTrigger>
              <SelectContent>{cameras.map(c => <SelectItem key={c.id} value={c.id}>{c.name}{c.location ? ` — ${c.location}` : ''}</SelectItem>)}</SelectContent>
            </Select>
            {cameras.length === 0 && <p className="text-xs text-amber-700 mt-1">No cameras registered yet — add one in /cctv first.</p>}
          </div>
          <div><Label>Clip URL *</Label><Input value={form.clip_url} onChange={e => setForm({ ...form, clip_url: e.target.value })} placeholder="https://… mp4 or playback link" data-testid="sec-link-clip-url" /></div>
          <MediaUpload value={form.thumbnail_url} onChange={(url) => setForm({ ...form, thumbnail_url: url })} purpose="photo" label="Thumbnail (optional)" testId="sec-link-clip-thumb" previewClassName="h-16" />
          <div className="grid grid-cols-2 gap-2">
            <div><Label>Captured at *</Label><Input type="datetime-local" value={form.captured_at} onChange={e => setForm({ ...form, captured_at: e.target.value })} /></div>
            <div><Label>Duration (s)</Label><Input type="number" min={0} value={form.duration_seconds} onChange={e => setForm({ ...form, duration_seconds: parseInt(e.target.value, 10) || 0 })} /></div>
          </div>
          <div><Label>Notes</Label><Input value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => { if (!form.camera_id) return toast.error('Camera required'); if (!form.clip_url) return toast.error('Clip URL required'); mut.mutate(); }} disabled={mut.isPending} data-testid="sec-link-clip-save">
            {mut.isPending && <Loader2 className="h-4 w-4 animate-spin mr-1" />}Link clip
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ==================== DINING ====================
const DIETARY_TAGS = ['vegetarian', 'vegan', 'halal', 'kosher', 'gluten_free', 'dairy_free', 'nut_free', 'low_sodium'];
const TAG_COLOR = {
  vegetarian: 'bg-green-100 text-green-800',
  vegan: 'bg-emerald-100 text-emerald-800',
  halal: 'bg-teal-100 text-teal-800',
  kosher: 'bg-indigo-100 text-indigo-800',
  gluten_free: 'bg-amber-100 text-amber-800',
  dairy_free: 'bg-sky-100 text-sky-800',
  nut_free: 'bg-orange-100 text-orange-800',
  low_sodium: 'bg-purple-100 text-purple-800',
};
const MEAL_ICON_COLOR = {
  breakfast: 'bg-orange-100 text-orange-700',
  lunch: 'bg-amber-100 text-amber-700',
  dinner: 'bg-purple-100 text-purple-700',
  snack: 'bg-pink-100 text-pink-700',
};

function DiningTab() {
  const qc = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState(null);
  const [upcomingOnly, setUpcomingOnly] = useState(true);
  const url = `/api/ops/dining/menus?limit=200${upcomingOnly ? '&upcoming_only=true' : ''}`;
  const { data: menus = [], isLoading } = useQuery({ queryKey: ['dining-menus', upcomingOnly], queryFn: () => api(url) });
  const { data: stats } = useQuery({ queryKey: ['dining-stats'], queryFn: () => api('/api/ops/dining/stats?days=30') });
  const servMut = useMutation({
    mutationFn: ({ id, count }) => api(`/api/ops/dining/menus/${id}/served`, { method: 'POST', body: JSON.stringify({ count: Number(count) }) }),
    onSuccess: () => { toast.success('Updated'); qc.invalidateQueries({ queryKey: ['dining-menus'] }); qc.invalidateQueries({ queryKey: ['dining-stats'] }); },
    onError: (e) => toast.error(e.message),
  });
  const delMut = useMutation({
    mutationFn: (id) => api(`/api/ops/dining/menus/${id}`, { method: 'DELETE' }),
    onSuccess: () => { toast.success('Deleted'); qc.invalidateQueries({ queryKey: ['dining-menus'] }); qc.invalidateQueries({ queryKey: ['dining-stats'] }); },
    onError: (e) => toast.error(e.message),
  });

  // Group menus by date for calendar-style view
  const byDate = menus.reduce((acc, m) => { (acc[m.meal_date] = acc[m.meal_date] || []).push(m); return acc; }, {});
  const dates = Object.keys(byDate).sort();

  return (
    <div className="space-y-4">
      {/* Stats hero */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="dining-stats">
          <Card><CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 rounded-lg bg-amber-100"><UtensilsCrossed className="h-5 w-5 text-amber-700" /></div>
            <div>
              <p className="text-[11px] uppercase tracking-wide font-semibold text-slate-500">Portions served (30d)</p>
              <p className="text-2xl font-bold text-amber-700">{stats.portions_served || 0}</p>
            </div>
          </CardContent></Card>
          <Card><CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 rounded-lg bg-slate-100"><ClipboardList className="h-5 w-5 text-slate-700" /></div>
            <div>
              <p className="text-[11px] uppercase tracking-wide font-semibold text-slate-500">Menus planned</p>
              <p className="text-2xl font-bold">{stats.menus || 0}</p>
            </div>
          </CardContent></Card>
          <Card><CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 rounded-lg bg-emerald-100"><Sparkles className="h-5 w-5 text-emerald-700" /></div>
            <div>
              <p className="text-[11px] uppercase tracking-wide font-semibold text-slate-500">Fulfillment</p>
              <p className="text-2xl font-bold text-emerald-700">{stats.portions_expected ? Math.round((stats.portions_served / stats.portions_expected) * 100) : 0}<span className="text-sm">%</span></p>
            </div>
          </CardContent></Card>
          <Card><CardContent className="p-4">
            <p className="text-[11px] uppercase tracking-wide font-semibold text-slate-500 mb-1.5">Top meal mix</p>
            <div className="flex flex-wrap gap-1">
              {Object.entries(stats.by_meal || {}).sort(([,a],[,b]) => b - a).slice(0, 3).map(([k, v]) => (
                <span key={k} className={`text-[10px] px-1.5 py-0.5 rounded ${MEAL_ICON_COLOR[k] || 'bg-slate-100 text-slate-700'} capitalize`}>{k} {v}</span>
              ))}
              {Object.keys(stats.by_meal || {}).length === 0 && <span className="text-xs text-slate-400">No data</span>}
            </div>
          </CardContent></Card>
        </div>
      )}

      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="text-base font-semibold flex items-center gap-2"><UtensilsCrossed className="h-4 w-4 text-amber-600" />Meal plan</h2>
        <div className="flex items-center gap-2">
          <label className="text-xs text-slate-600 flex items-center gap-1.5">
            <input type="checkbox" checked={upcomingOnly} onChange={e => setUpcomingOnly(e.target.checked)} className="rounded" data-testid="dining-upcoming-only" />
            Upcoming only
          </label>
          <Button onClick={() => setAdding(true)} className="bg-amber-600 hover:bg-amber-700" data-testid="dining-add-menu"><Plus className="h-4 w-4 mr-1" /> New menu</Button>
        </div>
      </div>

      {isLoading ? <Card><CardContent className="p-12 text-center text-slate-400"><Loader2 className="h-5 w-5 animate-spin inline mr-2" />Loading…</CardContent></Card>
       : dates.length === 0 ? <Card><CardContent className="p-16 text-center text-slate-400"><UtensilsCrossed className="h-12 w-12 mx-auto mb-3 opacity-50" /><p className="text-sm">No menus planned yet</p></CardContent></Card>
       : (
        <div className="space-y-3">
          {dates.map(date => {
            const dayMenus = byDate[date].sort((a, b) => ['breakfast','lunch','dinner','snack'].indexOf(a.meal_type) - ['breakfast','lunch','dinner','snack'].indexOf(b.meal_type));
            const d = new Date(date + 'T12:00:00');
            const isToday = date === new Date().toISOString().slice(0, 10);
            return (
              <div key={date} className={`rounded-xl border ${isToday ? 'border-amber-300 bg-amber-50/30' : 'border-slate-200 bg-white'} overflow-hidden`}>
                <div className={`px-4 py-2 flex items-center justify-between ${isToday ? 'bg-amber-100/60' : 'bg-slate-50'} border-b`}>
                  <p className="font-semibold text-sm flex items-center gap-2">
                    {d.toLocaleDateString(undefined, { weekday: 'long', month: 'short', day: 'numeric' })}
                    {isToday && <Badge className="bg-amber-600 text-white text-[10px]">Today</Badge>}
                  </p>
                  <p className="text-[11px] text-slate-500">{dayMenus.length} meal{dayMenus.length === 1 ? '' : 's'}</p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 p-2">
                  {dayMenus.map(m => {
                    const served = m.served_count || 0;
                    const expected = m.expected_count || 0;
                    const pct = expected > 0 ? Math.min(100, Math.round((served / expected) * 100)) : 0;
                    return (
                      <div key={m.id} className="border rounded-lg p-3 hover:shadow-sm transition" data-testid={`dining-menu-${m.id}`}>
                        <div className="flex items-start justify-between gap-2 mb-1.5">
                          <div className="flex items-center gap-2">
                            <span className={`text-[10px] uppercase tracking-wide font-bold px-2 py-0.5 rounded ${MEAL_ICON_COLOR[m.meal_type] || 'bg-slate-100 text-slate-700'}`}>{m.meal_type}</span>
                          </div>
                          <div className="flex gap-1">
                            <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => setEditing(m)} data-testid={`dining-edit-${m.id}`}><Edit3 className="h-3 w-3" /></Button>
                            <Button variant="ghost" size="icon" className="h-6 w-6 text-rose-600" onClick={() => { if (window.confirm('Delete this menu?')) delMut.mutate(m.id); }} data-testid={`dining-delete-${m.id}`}><Trash2 className="h-3 w-3" /></Button>
                          </div>
                        </div>
                        <p className="text-sm text-slate-700 mb-1.5">{(m.items || []).join(' · ') || <span className="text-slate-400 italic">No items</span>}</p>
                        {(m.dietary_tags || []).length > 0 && (
                          <div className="flex flex-wrap gap-1 mb-2">
                            {m.dietary_tags.map(t => <span key={t} className={`text-[10px] px-1.5 py-0.5 rounded ${TAG_COLOR[t] || 'bg-slate-100'} capitalize`}>{t.replace(/_/g, ' ')}</span>)}
                          </div>
                        )}
                        <div className="flex items-center justify-between gap-2 pt-2 border-t">
                          <div className="flex-1">
                            <div className="flex items-center justify-between text-[11px] text-slate-500 mb-1">
                              <span>Served / Expected</span>
                              <span className="font-bold text-amber-700">{served} / {expected || '—'}</span>
                            </div>
                            {expected > 0 && (
                              <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                                <div className={`h-full ${pct >= 100 ? 'bg-emerald-500' : pct >= 70 ? 'bg-amber-500' : 'bg-rose-400'}`} style={{ width: `${pct}%` }} />
                              </div>
                            )}
                          </div>
                          <Input type="number" min={0} defaultValue={served} onBlur={e => { const v = parseInt(e.target.value, 10); if (v !== served) servMut.mutate({ id: m.id, count: v }); }} className="h-7 w-16 text-xs" data-testid={`dining-served-${m.id}`} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {adding && <DiningAddDialog onClose={() => setAdding(false)} onAdded={() => { qc.invalidateQueries({ queryKey: ['dining-menus'] }); qc.invalidateQueries({ queryKey: ['dining-stats'] }); setAdding(false); }} />}
      {editing && <DiningEditDialog menu={editing} onClose={() => setEditing(null)} onSaved={() => { qc.invalidateQueries({ queryKey: ['dining-menus'] }); qc.invalidateQueries({ queryKey: ['dining-stats'] }); setEditing(null); }} />}
    </div>
  );
}

function DiningTagPicker({ value, onChange }) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {DIETARY_TAGS.map(t => {
        const active = value.includes(t);
        return (
          <button
            key={t} type="button"
            onClick={() => onChange(active ? value.filter(x => x !== t) : [...value, t])}
            className={`text-[10px] px-2 py-1 rounded-full border transition capitalize ${active ? `${TAG_COLOR[t]} border-transparent` : 'bg-white text-slate-500 border-slate-200 hover:border-slate-400'}`}
            data-testid={`dining-tag-${t}`}
          >
            {t.replace(/_/g, ' ')}
          </button>
        );
      })}
    </div>
  );
}

function DiningAddDialog({ onClose, onAdded }) {
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({ meal_date: today, meal_type: 'lunch', items: '', notes: '', expected_count: 0, dietary_tags: [] });
  const mut = useMutation({
    mutationFn: () => api('/api/ops/dining/menus', { method: 'POST', body: JSON.stringify({
      ...form,
      items: form.items.split(',').map(s => s.trim()).filter(Boolean),
      expected_count: parseInt(form.expected_count, 10) || 0,
    }) }),
    onSuccess: () => { toast.success('Menu added'); onAdded?.(); },
    onError: (e) => toast.error(e.message),
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="dining-add-dialog">
        <DialogHeader><DialogTitle>New menu</DialogTitle><DialogDescription>Plan a meal — items as a comma-separated list, tag for dietary needs.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            <div className="col-span-2"><Label>Date *</Label><Input type="date" value={form.meal_date} onChange={e => setForm({ ...form, meal_date: e.target.value })} data-testid="dining-add-date" /></div>
            <div>
              <Label>Meal</Label>
              <Select value={form.meal_type} onValueChange={v => setForm({ ...form, meal_type: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{['breakfast','lunch','dinner','snack'].map(t => <SelectItem key={t} value={t} className="capitalize">{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div><Label>Items</Label><Textarea rows={2} value={form.items} onChange={e => setForm({ ...form, items: e.target.value })} placeholder="Rice, Chicken, Vegetables" data-testid="dining-add-items" /></div>
          <div><Label>Expected portions</Label><Input type="number" min={0} value={form.expected_count} onChange={e => setForm({ ...form, expected_count: e.target.value })} data-testid="dining-add-expected" /></div>
          <div><Label>Dietary tags</Label><DiningTagPicker value={form.dietary_tags} onChange={tags => setForm({ ...form, dietary_tags: tags })} /></div>
          <div><Label>Notes</Label><Input value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => mut.mutate()} disabled={mut.isPending} className="bg-amber-600 hover:bg-amber-700" data-testid="dining-add-save">
            {mut.isPending && <Loader2 className="h-4 w-4 animate-spin mr-1" />}Add menu
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DiningEditDialog({ menu, onClose, onSaved }) {
  const [form, setForm] = useState({
    meal_type: menu.meal_type || 'lunch',
    items: (menu.items || []).join(', '),
    notes: menu.notes || '',
    expected_count: menu.expected_count || 0,
    dietary_tags: menu.dietary_tags || [],
  });
  const mut = useMutation({
    mutationFn: () => api(`/api/ops/dining/menus/${menu.id}`, { method: 'PUT', body: JSON.stringify({
      ...form,
      items: form.items.split(',').map(s => s.trim()).filter(Boolean),
      expected_count: parseInt(form.expected_count, 10) || 0,
    }) }),
    onSuccess: () => { toast.success('Saved'); onSaved?.(); },
    onError: (e) => toast.error(e.message),
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="dining-edit-dialog">
        <DialogHeader><DialogTitle>Edit menu — {menu.meal_date}</DialogTitle><DialogDescription>Adjust items, tags, expected portions.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Meal</Label>
            <Select value={form.meal_type} onValueChange={v => setForm({ ...form, meal_type: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{['breakfast','lunch','dinner','snack'].map(t => <SelectItem key={t} value={t} className="capitalize">{t}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Items</Label><Textarea rows={2} value={form.items} onChange={e => setForm({ ...form, items: e.target.value })} /></div>
          <div><Label>Expected portions</Label><Input type="number" min={0} value={form.expected_count} onChange={e => setForm({ ...form, expected_count: e.target.value })} /></div>
          <div><Label>Dietary tags</Label><DiningTagPicker value={form.dietary_tags} onChange={tags => setForm({ ...form, dietary_tags: tags })} /></div>
          <div><Label>Notes</Label><Input value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => mut.mutate()} disabled={mut.isPending} data-testid="dining-edit-save">
            {mut.isPending && <Loader2 className="h-4 w-4 animate-spin mr-1" />}Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ==================== CCTV ====================
function CctvTab() {
  const qc = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [addingClip, setAddingClip] = useState(false);
  const [view, setView] = useState('cameras');   // 'cameras' | 'clips'
  const { data: cams = [], isLoading } = useQuery({ queryKey: ['cctv-cameras'], queryFn: () => api('/api/ops/cctv/cameras') });
  const { data: clips = [], isLoading: clipsLoading } = useQuery({ queryKey: ['cctv-clips'], queryFn: () => api('/api/ops/cctv/clips?limit=200') });
  const delMut = useMutation({
    mutationFn: (id) => api(`/api/ops/cctv/cameras/${id}`, { method: 'DELETE' }),
    onSuccess: () => { toast.success('Removed'); qc.invalidateQueries({ queryKey: ['cctv-cameras'] }); },
    onError: (e) => toast.error(e.message),
  });
  const delClipMut = useMutation({
    mutationFn: (id) => api(`/api/ops/cctv/clips/${id}`, { method: 'DELETE' }),
    onSuccess: () => { toast.success('Clip removed'); qc.invalidateQueries({ queryKey: ['cctv-clips'] }); },
    onError: (e) => toast.error(e.message),
  });

  const onlineCount = cams.filter(c => c.status === 'online').length;
  const offlineCount = cams.filter(c => c.status !== 'online').length;

  return (
    <div className="space-y-4">
      {/* Stats hero */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card><CardContent className="p-4 flex items-center gap-3">
          <div className="p-2 rounded-lg bg-emerald-100"><Video className="h-5 w-5 text-emerald-700" /></div>
          <div><p className="text-[11px] uppercase tracking-wide font-semibold text-slate-500">Online</p><p className="text-2xl font-bold text-emerald-700">{onlineCount}</p></div>
        </CardContent></Card>
        <Card><CardContent className="p-4 flex items-center gap-3">
          <div className="p-2 rounded-lg bg-rose-100"><Video className="h-5 w-5 text-rose-700" /></div>
          <div><p className="text-[11px] uppercase tracking-wide font-semibold text-slate-500">Offline / maintenance</p><p className="text-2xl font-bold text-rose-700">{offlineCount}</p></div>
        </CardContent></Card>
        <Card><CardContent className="p-4 flex items-center gap-3">
          <div className="p-2 rounded-lg bg-indigo-100"><Camera className="h-5 w-5 text-indigo-700" /></div>
          <div><p className="text-[11px] uppercase tracking-wide font-semibold text-slate-500">Total cameras</p><p className="text-2xl font-bold">{cams.length}</p></div>
        </CardContent></Card>
        <Card><CardContent className="p-4 flex items-center gap-3">
          <div className="p-2 rounded-lg bg-purple-100"><Film className="h-5 w-5 text-purple-700" /></div>
          <div><p className="text-[11px] uppercase tracking-wide font-semibold text-slate-500">Clips logged</p><p className="text-2xl font-bold text-purple-700">{clips.length}</p></div>
        </CardContent></Card>
      </div>

      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Button size="sm" variant={view === 'cameras' ? 'default' : 'outline'} onClick={() => setView('cameras')} data-testid="cctv-view-cameras">Cameras</Button>
          <Button size="sm" variant={view === 'clips' ? 'default' : 'outline'} onClick={() => setView('clips')} data-testid="cctv-view-clips">Clip log{clips.length > 0 && <Badge className="ml-2 bg-purple-100 text-purple-800 hover:bg-purple-100">{clips.length}</Badge>}</Button>
        </div>
        {view === 'cameras' ? (
          <Button onClick={() => setAdding(true)} className="bg-indigo-600 hover:bg-indigo-700" data-testid="cctv-add-camera"><Plus className="h-4 w-4 mr-1" /> Add camera</Button>
        ) : (
          <Button onClick={() => setAddingClip(true)} className="bg-purple-600 hover:bg-purple-700" data-testid="cctv-add-clip"><Plus className="h-4 w-4 mr-1" /> Log clip</Button>
        )}
      </div>

      {view === 'cameras' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {isLoading ? <div className="p-8 text-center text-slate-400 col-span-3"><Loader2 className="h-4 w-4 animate-spin inline mr-2" />Loading…</div>
           : cams.length === 0 ? <Card className="col-span-3"><CardContent className="p-16 text-center text-slate-400"><Video className="h-12 w-12 mx-auto mb-3 opacity-50" /><p className="text-sm">No cameras configured</p></CardContent></Card>
           : cams.map(c => (
              <Card key={c.id} className="overflow-hidden hover:shadow-md transition-shadow" data-testid={`cctv-camera-${c.id}`}>
                <div className="aspect-video bg-gradient-to-br from-slate-900 to-slate-800 flex items-center justify-center relative">
                  <Video className="h-10 w-10 text-slate-600" />
                  <div className="absolute top-2 left-2 flex items-center gap-1.5">
                    <span className={`h-2 w-2 rounded-full ${c.status === 'online' ? 'bg-emerald-400 animate-pulse' : c.status === 'maintenance' ? 'bg-amber-400' : 'bg-rose-400'}`} />
                    <span className="text-[10px] text-white uppercase tracking-wide font-medium">{c.status}</span>
                  </div>
                </div>
                <CardContent className="p-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="font-semibold truncate">{c.name}</p>
                      <p className="text-xs text-slate-500 flex items-center gap-1 truncate"><MapPin className="h-3 w-3 shrink-0" />{c.location || '—'}</p>
                    </div>
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-rose-600 shrink-0" onClick={() => { if (window.confirm(`Delete ${c.name}?`)) delMut.mutate(c.id); }} data-testid={`cctv-delete-${c.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                  </div>
                  {c.stream_url && <a href={c.stream_url} target="_blank" rel="noopener noreferrer" className="text-xs text-indigo-700 hover:underline truncate block mt-1.5 font-medium">→ View live stream</a>}
                </CardContent>
              </Card>
            ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {clipsLoading ? <div className="p-8 text-center text-slate-400 col-span-3"><Loader2 className="h-4 w-4 animate-spin inline mr-2" />Loading…</div>
           : clips.length === 0 ? <Card className="col-span-3"><CardContent className="p-16 text-center text-slate-400"><Film className="h-12 w-12 mx-auto mb-3 opacity-50" /><p className="text-sm">No clips logged yet</p><p className="text-xs mt-1">Log evidence clips here, or attach them from Security incidents.</p></CardContent></Card>
           : clips.map(c => (
              <Card key={c.id} className="overflow-hidden hover:shadow-md transition" data-testid={`cctv-clip-${c.id}`}>
                <a href={c.clip_url} target="_blank" rel="noopener noreferrer" className="block aspect-video bg-gradient-to-br from-slate-900 to-slate-800 relative">
                  {c.thumbnail_url ? <img src={c.thumbnail_url} alt="" className="w-full h-full object-cover" /> : <div className="absolute inset-0 flex items-center justify-center"><Film className="h-10 w-10 text-purple-400" /></div>}
                  {c.incident_id && <div className="absolute top-2 left-2"><Badge className="bg-red-600 text-white text-[10px]">Linked to incident</Badge></div>}
                  {c.duration_seconds > 0 && <div className="absolute bottom-2 right-2 bg-black/70 text-white text-[10px] px-1.5 py-0.5 rounded">{c.duration_seconds}s</div>}
                </a>
                <CardContent className="p-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="font-medium text-sm truncate">{c.camera_name || 'Camera'}</p>
                      <p className="text-[11px] text-slate-500">{c.captured_at?.slice(0, 16).replace('T', ' ')}</p>
                      {c.camera_location && <p className="text-[11px] text-slate-400 truncate">📍 {c.camera_location}</p>}
                    </div>
                    <Button variant="ghost" size="icon" className="h-6 w-6 text-rose-600 shrink-0" onClick={() => { if (window.confirm('Delete this clip?')) delClipMut.mutate(c.id); }} data-testid={`cctv-clip-delete-${c.id}`}><Trash2 className="h-3 w-3" /></Button>
                  </div>
                  {c.notes && <p className="text-[11px] text-slate-600 mt-1.5 line-clamp-2">{c.notes}</p>}
                </CardContent>
              </Card>
            ))}
        </div>
      )}
      {adding && <CctvAddDialog onClose={() => setAdding(false)} onAdded={() => { qc.invalidateQueries({ queryKey: ['cctv-cameras'] }); setAdding(false); }} />}
      {addingClip && <CctvAddClipDialog cameras={cams} onClose={() => setAddingClip(false)} onAdded={() => { qc.invalidateQueries({ queryKey: ['cctv-clips'] }); setAddingClip(false); }} />}
    </div>
  );
}

function CctvAddClipDialog({ cameras, onClose, onAdded }) {
  const [form, setForm] = useState({ camera_id: '', clip_url: '', thumbnail_url: '', captured_at: new Date().toISOString().slice(0, 16), duration_seconds: 0, notes: '', incident_id: '' });
  const mut = useMutation({
    mutationFn: () => api('/api/ops/cctv/clips', { method: 'POST', body: JSON.stringify({
      ...form,
      captured_at: new Date(form.captured_at).toISOString(),
      duration_seconds: parseInt(form.duration_seconds, 10) || 0,
    }) }),
    onSuccess: () => { toast.success('Clip logged'); onAdded?.(); },
    onError: (e) => toast.error(e.message),
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="cctv-add-clip-dialog">
        <DialogHeader><DialogTitle>Log evidence clip</DialogTitle><DialogDescription>Add a recorded clip to the audit log. Optionally link to a security incident.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Camera *</Label>
            <Select value={form.camera_id} onValueChange={v => setForm({ ...form, camera_id: v })}>
              <SelectTrigger data-testid="cctv-clip-camera"><SelectValue placeholder="Choose camera" /></SelectTrigger>
              <SelectContent>{cameras.map(c => <SelectItem key={c.id} value={c.id}>{c.name}{c.location ? ` — ${c.location}` : ''}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Clip URL *</Label><Input value={form.clip_url} onChange={e => setForm({ ...form, clip_url: e.target.value })} placeholder="https://… mp4 or playback link" data-testid="cctv-clip-url" /></div>
          <MediaUpload value={form.thumbnail_url} onChange={(url) => setForm({ ...form, thumbnail_url: url })} purpose="photo" label="Thumbnail (optional)" testId="cctv-clip-thumb" previewClassName="h-16" />
          <div className="grid grid-cols-2 gap-2">
            <div><Label>Captured at *</Label><Input type="datetime-local" value={form.captured_at} onChange={e => setForm({ ...form, captured_at: e.target.value })} /></div>
            <div><Label>Duration (s)</Label><Input type="number" min={0} value={form.duration_seconds} onChange={e => setForm({ ...form, duration_seconds: e.target.value })} /></div>
          </div>
          <div><Label>Linked incident ID (optional)</Label><Input value={form.incident_id} onChange={e => setForm({ ...form, incident_id: e.target.value })} placeholder="Paste incident _id" /></div>
          <div><Label>Notes</Label><Input value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => { if (!form.camera_id) return toast.error('Camera required'); if (!form.clip_url) return toast.error('Clip URL required'); mut.mutate(); }} disabled={mut.isPending} className="bg-purple-600 hover:bg-purple-700" data-testid="cctv-clip-save">
            {mut.isPending && <Loader2 className="h-4 w-4 animate-spin mr-1" />}Log clip
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CctvAddDialog({ onClose, onAdded }) {
  const [form, setForm] = useState({ name: '', location: '', stream_url: '', status: 'online' });
  const mut = useMutation({
    mutationFn: () => api('/api/ops/cctv/cameras', { method: 'POST', body: JSON.stringify(form) }),
    onSuccess: () => { toast.success('Camera added'); onAdded?.(); },
    onError: (e) => toast.error(e.message),
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="cctv-add-dialog">
        <DialogHeader><DialogTitle>Add camera</DialogTitle><DialogDescription>Register a camera and its live stream URL.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div><Label>Name *</Label><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Lobby Cam 1" data-testid="cctv-add-name" /></div>
          <div><Label>Location</Label><Input value={form.location} onChange={e => setForm({ ...form, location: e.target.value })} placeholder="Main Lobby" /></div>
          <div><Label>Stream URL</Label><Input value={form.stream_url} onChange={e => setForm({ ...form, stream_url: e.target.value })} placeholder="rtsp://… or https://…" /></div>
          <div>
            <Label>Status</Label>
            <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{['online','offline','maintenance'].map(s => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => { if (!form.name) return toast.error('Name required'); mut.mutate(); }} disabled={mut.isPending} data-testid="cctv-add-save">
            {mut.isPending && <Loader2 className="h-4 w-4 animate-spin mr-1" />}Add
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ==================== HOUSING ====================
function HousingTab() {
  const qc = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [checkinFor, setCheckinFor] = useState(null);
  const [view, setView] = useState('rooms');     // 'rooms' | 'bookings' | 'maintenance' | 'cleaning'
  const [maintAdd, setMaintAdd] = useState(false);
  const [cleanAdd, setCleanAdd] = useState(false);
  const { data: rooms = [], isLoading } = useQuery({ queryKey: ['housing-rooms'], queryFn: () => api('/api/ops/housing/rooms') });
  const { data: bookings = [] } = useQuery({ queryKey: ['housing-bookings'], queryFn: () => api('/api/ops/housing/bookings') });
  const { data: maintenance = [] } = useQuery({ queryKey: ['housing-maintenance'], queryFn: () => api('/api/ops/housing/maintenance').catch(() => []) });
  const { data: cleaning = [] } = useQuery({ queryKey: ['housing-cleaning'], queryFn: () => api('/api/ops/housing/cleaning').catch(() => []) });
  const pendingCount = bookings.filter(b => b.status === 'pending').length;
  const openMaintCount = maintenance.filter(m => m.status === 'open' || m.status === 'in_progress').length;
  const pendingCleanCount = cleaning.filter(c => c.status !== 'completed').length;
  const checkoutMut = useMutation({
    mutationFn: ({ roomId, checkinId }) => api(`/api/ops/housing/rooms/${roomId}/checkout/${checkinId}`, { method: 'POST' }),
    onSuccess: () => { toast.success('Checked out'); qc.invalidateQueries({ queryKey: ['housing-rooms'] }); },
    onError: (e) => toast.error(e.message),
  });
  const statusMut = useMutation({
    mutationFn: ({ id, new_status }) => api(`/api/ops/housing/bookings/${id}/status`, { method: 'PUT', body: JSON.stringify({ new_status }) }),
    onSuccess: () => { toast.success('Updated'); qc.invalidateQueries({ queryKey: ['housing-bookings'] }); qc.invalidateQueries({ queryKey: ['housing-rooms'] }); },
    onError: (e) => toast.error(e.message),
  });
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2 flex-wrap">
          <Button size="sm" variant={view === 'rooms' ? 'default' : 'outline'} onClick={() => setView('rooms')} data-testid="housing-view-rooms">Rooms</Button>
          <Button size="sm" variant={view === 'bookings' ? 'default' : 'outline'} onClick={() => setView('bookings')} data-testid="housing-view-bookings">
            Public bookings{pendingCount > 0 && <Badge className="ml-2 bg-amber-100 text-amber-800 hover:bg-amber-100">{pendingCount}</Badge>}
          </Button>
          <Button size="sm" variant={view === 'maintenance' ? 'default' : 'outline'} onClick={() => setView('maintenance')} data-testid="housing-view-maintenance">
            <Wrench className="h-3.5 w-3.5 mr-1" />Maintenance{openMaintCount > 0 && <Badge className="ml-2 bg-rose-100 text-rose-800 hover:bg-rose-100">{openMaintCount}</Badge>}
          </Button>
          <Button size="sm" variant={view === 'cleaning' ? 'default' : 'outline'} onClick={() => setView('cleaning')} data-testid="housing-view-cleaning">
            <Brush className="h-3.5 w-3.5 mr-1" />Cleaning{pendingCleanCount > 0 && <Badge className="ml-2 bg-sky-100 text-sky-800 hover:bg-sky-100">{pendingCleanCount}</Badge>}
          </Button>
          <a href="/housing/book" target="_blank" rel="noopener noreferrer" className="text-xs text-teal-700 hover:underline ml-1">Public page → /housing/book</a>
        </div>
        {view === 'rooms' && <Button onClick={() => setAdding(true)} className="bg-teal-600 hover:bg-teal-700" data-testid="housing-add-room"><Plus className="h-4 w-4 mr-1" /> Add room</Button>}
        {view === 'maintenance' && <Button onClick={() => setMaintAdd(true)} className="bg-rose-600 hover:bg-rose-700" data-testid="housing-add-maintenance"><Plus className="h-4 w-4 mr-1" /> New request</Button>}
        {view === 'cleaning' && <Button onClick={() => setCleanAdd(true)} className="bg-sky-600 hover:bg-sky-700" data-testid="housing-add-cleaning"><Plus className="h-4 w-4 mr-1" /> Schedule cleaning</Button>}
      </div>

      {view === 'rooms' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {isLoading ? <div className="p-8 text-center text-slate-400 col-span-2"><Loader2 className="h-4 w-4 animate-spin inline mr-2" />Loading…</div>
           : rooms.length === 0 ? <div className="p-12 text-center text-slate-400 col-span-2"><Home className="h-10 w-10 mx-auto mb-2" />No rooms configured</div>
           : rooms.map(r => {
              const occ = r.occupants || [];
              const cap = r.capacity || 1;
              return (
                <Card key={r.id} data-testid={`housing-room-${r.id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <div className="font-medium flex items-center gap-1">
                          {r.name}
                          {r.is_public && <Badge variant="outline" className="text-[10px] text-teal-700 border-teal-200 bg-teal-50" data-testid={`housing-public-badge-${r.id}`}>Public</Badge>}
                        </div>
                        <p className="text-xs text-slate-500">
                          {r.building || '—'} · capacity {cap}
                          {r.nightly_rate > 0 && ` · ${r.currency || 'USD'} ${r.nightly_rate}/night`}
                        </p>
                      </div>
                      <Badge variant="outline" className={`text-[10px] capitalize ${r.status === 'available' ? 'text-emerald-700 border-emerald-200 bg-emerald-50' : r.status === 'partial' ? 'text-amber-700' : 'text-rose-700'}`}>{r.status}</Badge>
                    </div>
                    {occ.length > 0 ? (
                      <ul className="space-y-1 mt-2">
                        {occ.map(o => (
                          <li key={o.id} className="flex justify-between items-center text-xs py-1 border-b last:border-0">
                            <span>{o.occupant_name}{o.occupant_email && <span className="text-slate-400"> · {o.occupant_email}</span>}</span>
                            <Button variant="ghost" size="sm" className="h-6 text-xs" onClick={() => checkoutMut.mutate({ roomId: r.id, checkinId: o.id })} data-testid={`housing-checkout-${o.id}`}>
                              <LogOutIcon className="h-3 w-3 mr-1" />Check out
                            </Button>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-xs text-slate-400 italic mt-2">No occupants</p>
                    )}
                    {occ.length < cap && (
                      <Button size="sm" variant="outline" className="w-full mt-2 text-xs" onClick={() => setCheckinFor(r)} data-testid={`housing-checkin-${r.id}`}><DoorOpen className="h-3 w-3 mr-1" />Check in</Button>
                    )}
                  </CardContent>
                </Card>
              );
            })}
        </div>
      )}

      {view === 'bookings' && (
        <Card><CardContent className="p-0">
          {bookings.length === 0 ? <div className="p-12 text-center text-slate-400"><CalendarCheck className="h-10 w-10 mx-auto mb-2" />No public bookings yet</div>
           : <table className="w-full text-sm">
              <thead><tr className="text-xs uppercase tracking-wide text-slate-500 border-b">
                <th className="text-left py-2 px-3">Ref</th><th className="text-left py-2 px-3">Guest</th><th className="text-left py-2 px-3">Room</th><th className="text-left py-2 px-3">Dates</th><th className="text-right py-2 px-3">Cost</th><th className="text-left py-2 px-3">Status</th><th className="text-right py-2 px-3">Actions</th>
              </tr></thead>
              <tbody>
                {bookings.map(b => (
                  <tr key={b.id} className="border-b last:border-0" data-testid={`housing-booking-${b.id}`}>
                    <td className="py-2 px-3 text-xs font-mono">{b.booking_reference}</td>
                    <td className="py-2 px-3 text-xs">{b.client_name}<br /><span className="text-slate-400">{b.client_email}</span></td>
                    <td className="py-2 px-3 text-xs">{b.room_name}</td>
                    <td className="py-2 px-3 text-xs">{b.check_in_date} → {b.check_out_date}<br /><span className="text-slate-400">{b.nights} night{b.nights === 1 ? '' : 's'} · {b.guests_count} guest{b.guests_count === 1 ? '' : 's'}</span></td>
                    <td className="py-2 px-3 text-right text-xs">{b.estimated_cost > 0 ? `${b.currency} ${b.estimated_cost}` : 'Free'}</td>
                    <td className="py-2 px-3"><Badge variant="outline" className={`text-[10px] capitalize ${b.status === 'pending' ? 'text-amber-700 border-amber-200 bg-amber-50' : b.status === 'confirmed' ? 'text-emerald-700 border-emerald-200 bg-emerald-50' : 'text-rose-700'}`}>{b.status}</Badge></td>
                    <td className="py-2 px-3 text-right">
                      {b.status === 'pending' && (
                        <>
                          <Button size="sm" className="h-7 text-xs mr-1 bg-emerald-600 hover:bg-emerald-700" onClick={() => statusMut.mutate({ id: b.id, new_status: 'confirmed' })} data-testid={`housing-confirm-${b.id}`}>
                            <CheckCircle2 className="h-3 w-3 mr-1" />Confirm
                          </Button>
                          <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => statusMut.mutate({ id: b.id, new_status: 'cancelled' })} data-testid={`housing-cancel-${b.id}`}>
                            Cancel
                          </Button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>}
        </CardContent></Card>
      )}

      {view === 'maintenance' && <HousingMaintenanceView rooms={rooms} requests={maintenance} />}
      {view === 'cleaning' && <HousingCleaningView rooms={rooms} cleanings={cleaning} />}

      {adding && <HousingAddDialog onClose={() => setAdding(false)} onAdded={() => { qc.invalidateQueries({ queryKey: ['housing-rooms'] }); setAdding(false); }} />}
      {checkinFor && <HousingCheckinDialog room={checkinFor} onClose={() => setCheckinFor(null)} onSaved={() => { qc.invalidateQueries({ queryKey: ['housing-rooms'] }); setCheckinFor(null); }} />}
      {maintAdd && <HousingMaintenanceDialog rooms={rooms} onClose={() => setMaintAdd(false)} onAdded={() => { qc.invalidateQueries({ queryKey: ['housing-maintenance'] }); setMaintAdd(false); }} />}
      {cleanAdd && <HousingCleaningDialog rooms={rooms} onClose={() => setCleanAdd(false)} onAdded={() => { qc.invalidateQueries({ queryKey: ['housing-cleaning'] }); setCleanAdd(false); }} />}
    </div>
  );
}

const MAINT_PRIORITY_COLOR = {
  low: 'bg-blue-100 text-blue-800',
  medium: 'bg-amber-100 text-amber-800',
  high: 'bg-rose-100 text-rose-800',
  urgent: 'bg-red-600 text-white animate-pulse',
};
const MAINT_STATUS_COLOR = {
  open: 'bg-rose-100 text-rose-800',
  in_progress: 'bg-indigo-100 text-indigo-800',
  completed: 'bg-emerald-100 text-emerald-800',
  cancelled: 'bg-slate-100 text-slate-600',
};

function HousingMaintenanceView({ rooms, requests }) {
  const qc = useQueryClient();
  const updMut = useMutation({
    mutationFn: ({ id, status }) => api(`/api/ops/housing/maintenance/${id}`, { method: 'PUT', body: JSON.stringify({ status }) }),
    onSuccess: () => { toast.success('Updated'); qc.invalidateQueries({ queryKey: ['housing-maintenance'] }); },
    onError: (e) => toast.error(e.message),
  });
  const delMut = useMutation({
    mutationFn: (id) => api(`/api/ops/housing/maintenance/${id}`, { method: 'DELETE' }),
    onSuccess: () => { toast.success('Deleted'); qc.invalidateQueries({ queryKey: ['housing-maintenance'] }); },
    onError: (e) => toast.error(e.message),
  });
  return (
    <Card><CardContent className="p-0">
      {requests.length === 0 ? <div className="p-16 text-center text-slate-400"><Wrench className="h-12 w-12 mx-auto mb-3 opacity-50" /><p className="text-sm">No maintenance requests</p></div>
       : <div className="divide-y">
          {requests.map(r => (
            <div key={r.id} className="p-3 hover:bg-slate-50 transition" data-testid={`housing-maint-${r.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-medium text-sm">{r.title}</p>
                    <Badge className={`text-[10px] capitalize ${MAINT_PRIORITY_COLOR[r.priority] || ''}`}>{r.priority}</Badge>
                    <Badge variant="outline" className={`text-[10px] capitalize ${MAINT_STATUS_COLOR[r.status] || ''}`}>{r.status?.replace('_',' ')}</Badge>
                  </div>
                  {r.description && <p className="text-xs text-slate-600 mt-1">{r.description}</p>}
                  <div className="flex flex-wrap items-center gap-3 mt-1.5 text-[11px] text-slate-500">
                    {r.room_name && <span className="flex items-center gap-1"><Home className="h-3 w-3" />{r.room_name}</span>}
                    {r.reported_by_name && <span className="flex items-center gap-1"><Users className="h-3 w-3" />{r.reported_by_name}</span>}
                    <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{new Date(r.created_at).toLocaleString()}</span>
                    {(r.photo_urls || []).length > 0 && (
                      <span className="flex items-center gap-1 text-indigo-600"><Camera className="h-3 w-3" />{r.photo_urls.length}</span>
                    )}
                  </div>
                  {(r.photo_urls || []).length > 0 && (
                    <div className="flex gap-1.5 mt-2">
                      {r.photo_urls.slice(0, 4).map((u, i) => (
                        <a key={i} href={u} target="_blank" rel="noopener noreferrer"><img src={u} alt="" className="h-12 w-12 object-cover rounded border" /></a>
                      ))}
                    </div>
                  )}
                </div>
                <div className="flex flex-col gap-1 shrink-0">
                  {r.status === 'open' && <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => updMut.mutate({ id: r.id, status: 'in_progress' })} data-testid={`housing-maint-start-${r.id}`}>Start</Button>}
                  {(r.status === 'open' || r.status === 'in_progress') && <Button size="sm" className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700" onClick={() => updMut.mutate({ id: r.id, status: 'completed' })} data-testid={`housing-maint-complete-${r.id}`}><CheckCircle2 className="h-3 w-3 mr-1" />Done</Button>}
                  <Button variant="ghost" size="icon" className="h-6 w-6 text-rose-600" onClick={() => { if (window.confirm('Delete this request?')) delMut.mutate(r.id); }} data-testid={`housing-maint-delete-${r.id}`}><Trash2 className="h-3 w-3" /></Button>
                </div>
              </div>
            </div>
          ))}
        </div>}
    </CardContent></Card>
  );
}

function HousingMaintenanceDialog({ rooms, onClose, onAdded }) {
  const [form, setForm] = useState({ room_id: '', title: '', description: '', priority: 'medium', photo_urls: '' });
  const mut = useMutation({
    mutationFn: () => api('/api/ops/housing/maintenance', { method: 'POST', body: JSON.stringify({
      ...form,
      photo_urls: form.photo_urls.split(',').map(s => s.trim()).filter(Boolean),
    }) }),
    onSuccess: () => { toast.success('Request created'); onAdded?.(); },
    onError: (e) => toast.error(e.message),
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="housing-maint-dialog">
        <DialogHeader><DialogTitle>New maintenance request</DialogTitle><DialogDescription>Log the issue, room, and how urgent the fix is.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div><Label>Title *</Label><Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="Leaking tap in bathroom" data-testid="housing-maint-title" /></div>
          <div>
            <Label>Room</Label>
            <Select value={form.room_id} onValueChange={v => setForm({ ...form, room_id: v })}>
              <SelectTrigger data-testid="housing-maint-room"><SelectValue placeholder="(optional)" /></SelectTrigger>
              <SelectContent>{rooms.map(r => <SelectItem key={r.id} value={r.id}>{r.name}{r.building ? ` — ${r.building}` : ''}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Priority</Label>
            <Select value={form.priority} onValueChange={v => setForm({ ...form, priority: v })}>
              <SelectTrigger data-testid="housing-maint-priority"><SelectValue /></SelectTrigger>
              <SelectContent>{['low','medium','high','urgent'].map(p => <SelectItem key={p} value={p} className="capitalize">{p}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Description</Label><Textarea rows={3} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} /></div>
          <div><Label>Photos</Label>
            <MediaUploadList
              value={(form.photo_urls || '').split(',').map(s => s.trim()).filter(Boolean)}
              onChange={(list) => setForm({ ...form, photo_urls: list.join(', ') })}
              purpose="photo" hint="Add a photo of the problem" testId="housing-maint-photos" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => { if (!form.title) return toast.error('Title required'); mut.mutate(); }} disabled={mut.isPending} className="bg-rose-600 hover:bg-rose-700" data-testid="housing-maint-save">
            {mut.isPending && <Loader2 className="h-4 w-4 animate-spin mr-1" />}Submit
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function HousingCleaningView({ rooms, cleanings }) {
  const qc = useQueryClient();
  const updMut = useMutation({
    mutationFn: ({ id, payload }) => api(`/api/ops/housing/cleaning/${id}`, { method: 'PUT', body: JSON.stringify(payload) }),
    onSuccess: () => { toast.success('Updated'); qc.invalidateQueries({ queryKey: ['housing-cleaning'] }); },
    onError: (e) => toast.error(e.message),
  });
  const delMut = useMutation({
    mutationFn: (id) => api(`/api/ops/housing/cleaning/${id}`, { method: 'DELETE' }),
    onSuccess: () => { toast.success('Deleted'); qc.invalidateQueries({ queryKey: ['housing-cleaning'] }); },
    onError: (e) => toast.error(e.message),
  });
  const toggleTask = (c, idx) => {
    const tasks = (c.tasks || []).map((t, i) => i === idx ? { ...t, done: !t.done } : t);
    updMut.mutate({ id: c.id, payload: { tasks } });
  };
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      {cleanings.length === 0 ? <Card className="col-span-2"><CardContent className="p-16 text-center text-slate-400"><Brush className="h-12 w-12 mx-auto mb-3 opacity-50" /><p className="text-sm">No cleaning tasks scheduled</p></CardContent></Card>
       : cleanings.map(c => {
          const total = (c.tasks || []).length;
          const done = (c.tasks || []).filter(t => t.done).length;
          const pct = total > 0 ? Math.round((done / total) * 100) : 0;
          return (
            <Card key={c.id} data-testid={`housing-clean-${c.id}`}>
              <CardContent className="p-4 space-y-2">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <p className="font-medium text-sm flex items-center gap-2"><Brush className="h-4 w-4 text-sky-600" />{c.room_name}</p>
                    <p className="text-[11px] text-slate-500">{c.cleaning_date}{c.assigned_to_name ? ` · ${c.assigned_to_name}` : ''}</p>
                  </div>
                  <Badge className={`text-[10px] capitalize ${c.status === 'completed' ? 'bg-emerald-100 text-emerald-800' : c.status === 'in_progress' ? 'bg-amber-100 text-amber-800' : 'bg-slate-100 text-slate-700'}`}>{c.status?.replace('_',' ')}</Badge>
                </div>
                <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                  <div className={`h-full ${pct === 100 ? 'bg-emerald-500' : 'bg-sky-500'} transition-all`} style={{ width: `${pct}%` }} />
                </div>
                <p className="text-[11px] text-slate-500">{done} / {total} tasks done · {pct}%</p>
                <ul className="space-y-1 max-h-48 overflow-y-auto pr-1">
                  {(c.tasks || []).map((t, i) => (
                    <li key={i} className="flex items-center gap-2 text-xs">
                      <input type="checkbox" checked={!!t.done} onChange={() => toggleTask(c, i)} disabled={c.status === 'completed'} className="rounded" data-testid={`housing-clean-task-${c.id}-${i}`} />
                      <span className={t.done ? 'line-through text-slate-400' : ''}>{t.label}</span>
                    </li>
                  ))}
                </ul>
                <div className="flex justify-between pt-1">
                  {c.status !== 'completed' ? (
                    <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => updMut.mutate({ id: c.id, payload: { status: c.status === 'pending' ? 'in_progress' : 'completed' } })} data-testid={`housing-clean-advance-${c.id}`}>
                      {c.status === 'pending' ? 'Start' : 'Mark complete'}
                    </Button>
                  ) : <span />}
                  <Button variant="ghost" size="icon" className="h-7 w-7 text-rose-600" onClick={() => { if (window.confirm('Delete this cleaning task?')) delMut.mutate(c.id); }} data-testid={`housing-clean-delete-${c.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                </div>
              </CardContent>
            </Card>
          );
        })}
    </div>
  );
}

function HousingCleaningDialog({ rooms, onClose, onAdded }) {
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({ room_id: '', cleaning_date: today, notes: '' });
  const mut = useMutation({
    mutationFn: () => api('/api/ops/housing/cleaning', { method: 'POST', body: JSON.stringify(form) }),
    onSuccess: () => { toast.success('Cleaning scheduled'); onAdded?.(); },
    onError: (e) => toast.error(e.message),
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="housing-clean-dialog">
        <DialogHeader><DialogTitle>Schedule room cleaning</DialogTitle><DialogDescription>Creates a checklist with the standard tasks for the chosen room.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Room *</Label>
            <Select value={form.room_id} onValueChange={v => setForm({ ...form, room_id: v })}>
              <SelectTrigger data-testid="housing-clean-room"><SelectValue placeholder="Choose room" /></SelectTrigger>
              <SelectContent>{rooms.map(r => <SelectItem key={r.id} value={r.id}>{r.name}{r.building ? ` — ${r.building}` : ''}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Date *</Label><Input type="date" value={form.cleaning_date} onChange={e => setForm({ ...form, cleaning_date: e.target.value })} data-testid="housing-clean-date" /></div>
          <div><Label>Notes</Label><Input value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} /></div>
          <p className="text-xs text-slate-500">Standard checklist (beds, bathroom, trash, dust, floors, amenities, windows, damage) is auto-attached.</p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => { if (!form.room_id) return toast.error('Room required'); mut.mutate(); }} disabled={mut.isPending} className="bg-sky-600 hover:bg-sky-700" data-testid="housing-clean-save">
            {mut.isPending && <Loader2 className="h-4 w-4 animate-spin mr-1" />}Schedule
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function HousingAddDialog({ onClose, onAdded }) {
  const [form, setForm] = useState({ name: '', building: '', capacity: 1, nightly_rate: 0, currency: 'USD', description: '', is_public: false });
  const mut = useMutation({
    mutationFn: () => api('/api/ops/housing/rooms', { method: 'POST', body: JSON.stringify({
      ...form,
      capacity: parseInt(form.capacity, 10) || 1,
      nightly_rate: parseFloat(form.nightly_rate) || 0,
    }) }),
    onSuccess: () => { toast.success('Room added'); onAdded?.(); },
    onError: (e) => toast.error(e.message),
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="housing-add-room-dialog">
        <DialogHeader><DialogTitle>Add room</DialogTitle><DialogDescription>Where it is, how many it sleeps, and whether the public can request it.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div><Label>Name *</Label><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Room 101" data-testid="housing-add-name" /></div>
          <div><Label>Building</Label><Input value={form.building} onChange={e => setForm({ ...form, building: e.target.value })} placeholder="North Block" /></div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label>Capacity</Label><Input type="number" min={1} value={form.capacity} onChange={e => setForm({ ...form, capacity: e.target.value })} /></div>
            <div><Label>Nightly rate</Label><Input type="number" step="0.01" min={0} value={form.nightly_rate} onChange={e => setForm({ ...form, nightly_rate: e.target.value })} /></div>
          </div>
          <div><Label>Description</Label><Textarea rows={2} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="Twin beds, en-suite, garden view…" /></div>
          <label className="flex items-center gap-2 text-sm border-t pt-3">
            <input type="checkbox" checked={form.is_public} onChange={e => setForm({ ...form, is_public: e.target.checked })} className="rounded border-slate-300" data-testid="housing-add-public" />
            <span>
              <span className="font-medium">Open for public booking</span>
              <span className="text-xs text-slate-500 block">External guests can request this room via <span className="font-mono">/housing/book</span></span>
            </span>
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => { if (!form.name) return toast.error('Name required'); mut.mutate(); }} disabled={mut.isPending} data-testid="housing-add-save">
            {mut.isPending && <Loader2 className="h-4 w-4 animate-spin mr-1" />}Add
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function HousingCheckinDialog({ room, onClose, onSaved }) {
  const [form, setForm] = useState({ occupant_name: '', occupant_email: '', check_in_date: new Date().toISOString().slice(0, 10), check_out_date: '', notes: '' });
  const mut = useMutation({
    mutationFn: () => api(`/api/ops/housing/rooms/${room.id}/checkin`, { method: 'POST', body: JSON.stringify(form) }),
    onSuccess: () => { toast.success('Checked in'); onSaved?.(); },
    onError: (e) => toast.error(e.message),
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="housing-checkin-dialog">
        <DialogHeader><DialogTitle>Check in to {room.name}</DialogTitle><DialogDescription>Who's moving in?</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div><Label>Name *</Label><Input value={form.occupant_name} onChange={e => setForm({ ...form, occupant_name: e.target.value })} data-testid="housing-checkin-name" /></div>
          <div><Label>Email</Label><Input type="email" value={form.occupant_email} onChange={e => setForm({ ...form, occupant_email: e.target.value })} /></div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label>Check in</Label><Input type="date" value={form.check_in_date} onChange={e => setForm({ ...form, check_in_date: e.target.value })} /></div>
            <div><Label>Expected check out</Label><Input type="date" value={form.check_out_date} onChange={e => setForm({ ...form, check_out_date: e.target.value })} /></div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => { if (!form.occupant_name) return toast.error('Name required'); mut.mutate(); }} disabled={mut.isPending} data-testid="housing-checkin-save">
            {mut.isPending && <Loader2 className="h-4 w-4 animate-spin mr-1" />}Check in
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


export default OpsPortal;
